/**
 * DP / Folha — Fase 1: Motor Legislativo + Cadastros Legais
 * GridProfile / NeuroSynx Ltda
 *
 * Página administrativa que reúne:
 *   • Dashboard (status legislativo + contadores)
 *   • Legislação vigente (INSS, IRRF, FGTS, SM, SF, VT)
 *   • Sindicatos (CRUD)
 *   • Centros de Custo (CRUD)
 *   • Certificados Digitais A1 (upload + listagem)
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { CurrencyInput } from './ui/currency-input';
import { PercentInput } from './ui/percent-input';
import { DatePicker } from './ui/date-picker';
import { useConfirm } from '../hooks/useConfirm';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Gavel, Scale, Landmark, Wallet, ShieldCheck, Building2,
  Plus, Pencil, Trash2, Upload, RefreshCw, AlertTriangle,
  CheckCircle2, XCircle, FileLock, Bus, Users2, Coins, UserCog, CalendarClock, History, Eye
} from 'lucide-react';

import CadastroTrabalhistaTab from './dp/CadastroTrabalhistaTab';
import SaudeOcupacionalTab from './dp/SaudeOcupacionalTab';
import { formatCNPJ, formatPhone } from '../lib/helpers';
import DisciplinarHistoricoTab from './dp/DisciplinarHistoricoTab';
import FolhaPagamentoTab from './dp/FolhaPagamentoTab';
import ConsignadoTab from './dp/ConsignadoTab';
import Decimo13FeriasRescisaoTab from './dp/Decimo13FeriasRescisaoTab';
import AfastamentoTab from './dp/AfastamentoTab';
import Cnab240Tab from './dp/Cnab240Tab';
import PjPagamentosTab from './dp/PjPagamentosTab';

const API = process.env.REACT_APP_BACKEND_URL;

const authHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

const fmtBRL = (v) =>
  v === null || v === undefined
    ? '—'
    : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtPct = (v) =>
  v === null || v === undefined ? '—' : `${(Number(v) * 100).toFixed(2).replace('.', ',')}%`;

const fmtDate = (s) => {
  if (!s) return '—';
  const value = String(s).slice(0, 10);
  if (value.includes('/')) return value;
  if (value.includes('-')) {
    const [y, m, d] = value.split('-');
    return `${d}/${m}/${y}`;
  }
  return value;
};

const fmtCompetencia = (mes, ano) => (
  mes && ano ? `${String(mes).padStart(2, '0')}/${ano}` : '—'
);

const isHoraExtraFormula = (formulaTipo) => /^hora_extra_\d+(?:_\d+)?$/.test(String(formulaTipo || ''));

const percentualHoraExtra = (formulaTipo) => {
  if (!isHoraExtraFormula(formulaTipo)) return null;
  const bruto = String(formulaTipo).replace('hora_extra_', '').replace('_', '.');
  const valor = Number(bruto);
  return Number.isFinite(valor) ? valor : null;
};

const EMPTY_DESCONTO_FAIXA = {
  limite_ate: '',
  desconto_tipo: 'fixo',
  desconto_valor: 0,
  desconto_teto_valor: 0,
  descricao: '',
};

const normalizeDescontoSindicalFaixas = (faixas = []) =>
  (faixas || []).map((faixa) => ({
    limite_ate: faixa?.limite_ate ?? '',
    desconto_tipo: faixa?.desconto_tipo || 'fixo',
    desconto_valor: Number(faixa?.desconto_valor || 0),
    desconto_teto_valor: Number(faixa?.desconto_teto_valor || 0),
    descricao: faixa?.descricao || '',
  }));

const buildDescontoSindicalPayload = (form, { espelhaMensalidade = false } = {}) => {
  const ativo = !!form.desconto_sindical_ativo;
  const tipo = ativo ? form.desconto_sindical_tipo : null;
  const payload = {
    desconto_sindical_tipo: tipo,
    desconto_sindical_valor: 0,
    desconto_sindical_teto_valor: null,
    desconto_sindical_faixa_base: null,
    desconto_sindical_faixas: [],
    desconto_sindical_descricao: ativo ? (form.desconto_sindical_descricao || null) : null,
  };

  if (!ativo) {
    if (espelhaMensalidade) payload.mensalidade_associado = 0;
    return payload;
  }

  if (tipo === 'faixa') {
    payload.desconto_sindical_faixa_base = form.desconto_sindical_faixa_base || 'salario_minimo';
    payload.desconto_sindical_faixas = (form.desconto_sindical_faixas || []).map((faixa) => ({
      limite_ate: faixa.limite_ate === '' || faixa.limite_ate === null || faixa.limite_ate === undefined
        ? null
        : Number(faixa.limite_ate),
      desconto_tipo: faixa.desconto_tipo || 'fixo',
      desconto_valor: Number(faixa.desconto_valor || 0),
      desconto_teto_valor: faixa.desconto_tipo === 'percentual'
        ? (Number(faixa.desconto_teto_valor || 0) || null)
        : null,
      descricao: faixa.descricao || null,
    }));
    if (espelhaMensalidade) payload.mensalidade_associado = 0;
    return payload;
  }

  payload.desconto_sindical_valor = Number(form.desconto_sindical_valor || 0);
  payload.desconto_sindical_teto_valor = tipo === 'percentual'
    ? (Number(form.desconto_sindical_teto_valor || 0) || null)
    : null;

  if (espelhaMensalidade) {
    payload.mensalidade_associado = tipo === 'fixo' ? Number(form.desconto_sindical_valor || 0) : 0;
  }

  return payload;
};

const validarFaixasDescontoSindical = (form) => {
  if (!form?.desconto_sindical_ativo || form?.desconto_sindical_tipo !== 'faixa') {
    return null;
  }

  const faixas = form.desconto_sindical_faixas || [];
  if (!faixas.length) {
    return 'Adicione ao menos uma faixa de desconto sindical.';
  }

  const ultimaFaixa = faixas[faixas.length - 1];
  const ultimaSemLimite = ultimaFaixa?.limite_ate === '' || ultimaFaixa?.limite_ate === null || ultimaFaixa?.limite_ate === undefined;
  if (!ultimaSemLimite) {
    return 'A última faixa deve ficar sem limite máximo para cobrir salários acima do último corte.';
  }

  return null;
};

const DescontoSindicalFaixasEditor = ({ form, setForm }) => {
  const faixaBase = form.desconto_sindical_faixa_base || 'salario_minimo';
  const faixas = form.desconto_sindical_faixas || [];

  const updateFaixa = (index, field, value) => {
    const nextFaixas = [...faixas];
    nextFaixas[index] = { ...nextFaixas[index], [field]: value };
    setForm({ ...form, desconto_sindical_faixas: nextFaixas });
  };

  const addFaixa = () => {
    setForm({ ...form, desconto_sindical_faixas: [...faixas, { ...EMPTY_DESCONTO_FAIXA }] });
  };

  const removeFaixa = (index) => {
    setForm({
      ...form,
      desconto_sindical_faixas: faixas.filter((_, faixaIndex) => faixaIndex !== index),
    });
  };

  return (
    <div className="col-span-2 rounded-md border border-dashed p-4 space-y-4 bg-white">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label>Base das faixas</Label>
          <Select
            value={faixaBase}
            onValueChange={(v) => setForm({ ...form, desconto_sindical_faixa_base: v })}
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="salario_minimo">Múltiplos do salário mínimo</SelectItem>
              <SelectItem value="valor_salario">Faixa salarial em reais</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end justify-end">
          <Button type="button" variant="outline" onClick={addFaixa}>
            <Plus className="h-4 w-4 mr-2" /> Adicionar faixa
          </Button>
        </div>
      </div>

      {faixas.length === 0 ? (
        <p className="text-xs text-slate-500">
          Nenhuma faixa cadastrada. Adicione ao menos uma faixa e deixe o limite vazio na última se quiser cobrir os salários acima.
        </p>
      ) : (
        <div className="space-y-3">
          {faixas.map((faixa, index) => (
            <div key={`faixa-${index}`} className="grid grid-cols-1 md:grid-cols-12 gap-3 rounded-md border p-3 bg-slate-50">
              <div className="md:col-span-3">
                <Label>Limite até</Label>
                {faixaBase === 'salario_minimo' ? (
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={faixa.limite_ate}
                    onChange={(e) => updateFaixa(index, 'limite_ate', e.target.value)}
                    placeholder="Ex.: 2"
                  />
                ) : (
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={faixa.limite_ate}
                    onChange={(e) => updateFaixa(index, 'limite_ate', e.target.value)}
                    placeholder="Ex.: 3000"
                  />
                )}
                <p className="mt-1 text-[11px] text-slate-500">
                  Deixe vazio apenas na última faixa para representar acima da faixa anterior.
                </p>
              </div>
              <div className="md:col-span-3">
                <Label>Desconto</Label>
                <Select
                  value={faixa.desconto_tipo || 'fixo'}
                  onValueChange={(v) => updateFaixa(index, 'desconto_tipo', v)}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixo">Valor fixo</SelectItem>
                    <SelectItem value="percentual">Percentual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-3">
                <Label>{faixa.desconto_tipo === 'percentual' ? 'Percentual (%)' : 'Valor (R$)'}</Label>
                {faixa.desconto_tipo === 'percentual' ? (
                  <PercentInput
                    value={faixa.desconto_valor}
                    onChange={(v) => updateFaixa(index, 'desconto_valor', v)}
                  />
                ) : (
                  <CurrencyInput
                    value={faixa.desconto_valor}
                    onChange={(v) => updateFaixa(index, 'desconto_valor', v)}
                  />
                )}
              </div>
              <div className="md:col-span-2">
                <Label>Teto (R$)</Label>
                {faixa.desconto_tipo === 'percentual' ? (
                  <CurrencyInput
                    value={faixa.desconto_teto_valor}
                    onChange={(v) => updateFaixa(index, 'desconto_teto_valor', v)}
                  />
                ) : (
                  <Input value="Não se aplica" disabled />
                )}
              </div>
              <div className="md:col-span-1 flex items-end justify-end">
                <Button type="button" variant="ghost" onClick={() => removeFaixa(index)}>
                  <Trash2 className="h-4 w-4 text-red-600" />
                </Button>
              </div>
              <div className="md:col-span-12">
                <Label>Descrição complementar da faixa</Label>
                <Input
                  value={faixa.descricao || ''}
                  onChange={(e) => updateFaixa(index, 'descricao', e.target.value)}
                  placeholder="Ex.: Até 2 salários mínimos"
                />
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-amber-700">
        Regra obrigatória: a última faixa deve ficar sem limite máximo. Exemplo: até 2 SM, até 4 SM, acima disso.
      </p>
    </div>
  );
};

// ============================================================================
// TAB: DASHBOARD
// ============================================================================
export const DashboardTab = () => {
  const [data, setData] = useState(null);
  const [alertasSaude, setAlertasSaude] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, ra] = await Promise.all([
        axios.get(`${API}/api/calculos-dp/dashboard/status`, authHeader()),
        axios.get(`${API}/api/calculos-dp/saude/alertas?janela_dias=30`, authHeader()),
      ]);
      setData(r.data);
      setAlertasSaude(ra.data);
    } catch (e) {
      toast.error('Erro ao carregar dashboard DP');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-6 text-slate-500">Carregando...</div>;
  if (!data) return null;

  const StatusPill = ({ ok, label }) => (
    <div
      data-testid={`dp-status-${label.toLowerCase().replace(/\s/g, '-')}`}
      className={`flex items-center gap-2 px-3 py-2 rounded-md border ${ok ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50'
        }`}
    >
      {ok ? (
        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
      ) : (
        <AlertTriangle className="h-4 w-4 text-amber-600" />
      )}
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <span className={`ml-auto text-xs font-semibold ${ok ? 'text-emerald-700' : 'text-amber-700'}`}>
        {ok ? 'Vigente' : 'Pendente'}
      </span>
    </div>
  );

  const Counter = ({ label, value, Icon }) => (
    <Card data-testid={`dp-counter-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
          <div className="text-2xl font-bold text-slate-800">{value}</div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gavel className="h-5 w-5 text-indigo-600" />
            Legislação Vigente — competência {fmtDate(data.competencia_referencia)}
          </CardTitle>
          <CardDescription>
            Status das tabelas legais ativas no sistema. Sem essas tabelas o motor de folha não opera.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <StatusPill ok={data.legislacao_vigente.inss} label="INSS" />
            <StatusPill ok={data.legislacao_vigente.irrf} label="IRRF" />
            <StatusPill ok={data.legislacao_vigente.fgts} label="FGTS" />
            <StatusPill ok={data.legislacao_vigente.salario_minimo} label="Salário Mínimo" />
            <StatusPill ok={data.legislacao_vigente.salario_familia} label="Salário Família" />
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Counter label="Sindicatos" value={data.contadores.sindicatos} Icon={Users2} />
        <Counter label="Convenções (CCT)" value={data.contadores.convencoes_coletivas} Icon={Scale} />
        <Counter label="Centros de Custo" value={data.contadores.centros_custo} Icon={Coins} />
        <Counter label="Certificados Digitais" value={data.contadores.certificados_digitais} Icon={FileLock} />
      </div>

      {data.alertas_certificados?.length > 0 && (
        <Card className="border-amber-300">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="h-5 w-5" />
              Certificados próximos do vencimento
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {data.alertas_certificados.map((c) => (
                <li key={c.id} className="flex items-center justify-between text-sm">
                  <span>{c.nome_titular} ({c.tipo})</span>
                  <Badge variant="outline" className="border-amber-400 text-amber-700">
                    vence em {c.dias_para_vencer} dias — {fmtDate(c.validade_fim)}
                  </Badge>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* WIDGET SAÚDE OCUPACIONAL — Fase 2.2 */}
      {alertasSaude && (alertasSaude.totais.asos_vencidos + alertasSaude.totais.asos_vencendo + alertasSaude.totais.epi_ca_vencidos + alertasSaude.totais.epi_ca_vencendo + alertasSaude.totais.epi_termos_pendentes > 0) && (
        <Card className="border-red-300 bg-red-50/30" data-testid="dp-widget-saude">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" />
              Alertas de Saúde Ocupacional (próximos 30 dias)
            </CardTitle>
            <CardDescription>Pendências de ASO, CA de EPI e termos de entrega que requerem atenção imediata.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 text-sm">
              {alertasSaude.totais.asos_vencidos > 0 && (
                <div className="p-2 rounded bg-red-100 border border-red-300">
                  <div className="text-xs text-red-700 uppercase">ASOs vencidos</div>
                  <div className="text-xl font-bold text-red-700">{alertasSaude.totais.asos_vencidos}</div>
                </div>
              )}
              {alertasSaude.totais.asos_vencendo > 0 && (
                <div className="p-2 rounded bg-amber-100 border border-amber-300">
                  <div className="text-xs text-amber-700 uppercase">ASOs vencendo</div>
                  <div className="text-xl font-bold text-amber-700">{alertasSaude.totais.asos_vencendo}</div>
                </div>
              )}
              {alertasSaude.totais.epi_ca_vencidos > 0 && (
                <div className="p-2 rounded bg-red-100 border border-red-300">
                  <div className="text-xs text-red-700 uppercase">CA EPI vencidos</div>
                  <div className="text-xl font-bold text-red-700">{alertasSaude.totais.epi_ca_vencidos}</div>
                </div>
              )}
              {alertasSaude.totais.epi_ca_vencendo > 0 && (
                <div className="p-2 rounded bg-amber-100 border border-amber-300">
                  <div className="text-xs text-amber-700 uppercase">CA EPI vencendo</div>
                  <div className="text-xl font-bold text-amber-700">{alertasSaude.totais.epi_ca_vencendo}</div>
                </div>
              )}
              {alertasSaude.totais.epi_termos_pendentes > 0 && (
                <div className="p-2 rounded bg-indigo-100 border border-indigo-300">
                  <div className="text-xs text-indigo-700 uppercase">Termos pendentes</div>
                  <div className="text-xl font-bold text-indigo-700">{alertasSaude.totais.epi_termos_pendentes}</div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-end">
        <Button
          variant="outline"
          onClick={load}
          data-testid="dp-dashboard-refresh-btn"
        >
          <RefreshCw className="h-4 w-4 mr-2" /> Atualizar
        </Button>
      </div>
    </div>
  );
};

// ============================================================================
// TAB: LEGISLAÇÃO VIGENTE (read-only para Fase 1)
// ============================================================================
// Endpoints/slug por tipo de tabela legal
const LEG_SLUG = {
  inss: 'inss', irrf: 'irrf', fgts: 'fgts',
  salario_minimo: 'salario-minimo', salario_familia: 'salario-familia',
  vt_limite: 'vt-limite',
  encargos_patronais: 'encargos-patronais',
};
export const LegislacaoTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editType, setEditType] = useState(null); // 'inss' | 'irrf' | 'fgts' | 'salario_minimo' | 'salario_familia' | 'vt_limite' | 'encargos_patronais'
  const [editMode, setEditMode] = useState('new'); // 'new' | 'edit'
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const [historicoUnificadoOpen, setHistoricoUnificadoOpen] = useState(false);
  const [historicoUnificado, setHistoricoUnificado] = useState([]);
  const [loadingHistoricoUnificado, setLoadingHistoricoUnificado] = useState(false);
  const [visualizarVersao, setVisualizarVersao] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/calculos-dp/legislacao/consolidado`, authHeader());
      setData(r.data);
    } catch (e) {
      toast.error('Erro ao carregar legislação');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadHistoricoUnificado = useCallback(async () => {
    setLoadingHistoricoUnificado(true);
    try {
      const r = await axios.get(`${API}/api/calculos-dp/legislacao/historico-completo`, authHeader());
      setHistoricoUnificado(r.data);
    } catch (e) {
      toast.error('Erro ao carregar histórico completo');
    } finally { setLoadingHistoricoUnificado(false); }
  }, []);

  // ---- Edição / criação ----
  const novosDefaults = {
    inss: { descricao: 'Tabela INSS', vigencia_inicio: '', vigencia_fim: null, teto: 0, aliquota_teto: 0.14, fonte_legal: '', observacoes: '', ativo: true, faixas: [] },
    irrf: { descricao: 'Tabela IRRF', vigencia_inicio: '', vigencia_fim: null, deducao_por_dependente: 0, desconto_simplificado_mensal: 0, limite_isencao_aposentado_65: 0, isencao_total_ate: 5000, reducao_parcial_ate: 7350, reducao_parcela_fixa: 978.62, reducao_aliquota: 0.133145, fonte_legal: '', observacoes: '', ativo: true, faixas: [] },
    fgts: { descricao: 'Tabela FGTS', vigencia_inicio: '', vigencia_fim: null, aliquota_normal: 0.08, aliquota_aprendiz: 0.02, aliquota_domestico: 0.08, multa_rescisoria_sem_justa_causa: 0.40, multa_rescisoria_acordo: 0.20, contribuicao_social_lei_complementar_110: 0, fonte_legal: '', observacoes: '', ativo: true },
    salario_minimo: { descricao: 'Salário Mínimo', vigencia_inicio: '', vigencia_fim: null, valor_nacional: 0, fonte_legal: '', observacoes: '', ativo: true },
    salario_familia: { descricao: 'Salário Família', vigencia_inicio: '', vigencia_fim: null, idade_maxima_filho: 14, fonte_legal: '', observacoes: '', ativo: true, faixas: [] },
    vt_limite: { descricao: 'Limite VT', vigencia_inicio: '', vigencia_fim: null, percentual_maximo_desconto: 0.06, fonte_legal: '', observacoes: '', ativo: true },
    encargos_patronais: { descricao: 'Encargos Patronais', vigencia_inicio: '', vigencia_fim: null, inss_patronal_aliquota: 0.20, rat_aliquota: 0.03, fap_padrao: 1.0, terceiros_aliquota: 0.058, fonte_legal: '', observacoes: '', ativo: true },
  };

  const abrirEdicao = (tipo, mode = 'new') => {
    setEditMode(mode);
    const base = JSON.parse(JSON.stringify(novosDefaults[tipo]));
    if (mode === 'edit' && data[tipo]) {
      setEditForm({ ...base, ...JSON.parse(JSON.stringify(data[tipo])) });
    } else {
      setEditForm(base);
    }
    setEditType(tipo);
  };
  const upd = (patch) => setEditForm((f) => ({ ...f, ...patch }));
  const updFaixa = (i, patch) => setEditForm((f) => ({ ...f, faixas: f.faixas.map((x, idx) => idx === i ? { ...x, ...patch } : x) }));
  const addFaixa = () => setEditForm((f) => {
    const ordem = (f.faixas?.length || 0) + 1;
    const nova = editType === 'inss' ? { ordem, valor_inicio: 0, valor_fim: 0, aliquota: 0 }
      : editType === 'irrf' ? { ordem, valor_inicio: 0, valor_fim: 0, aliquota: 0, deducao: 0 }
        : { ordem, valor_max_remuneracao: 0, valor_cota_por_filho: 0 };
    return { ...f, faixas: [...(f.faixas || []), nova] };
  });
  const delFaixa = (i) => setEditForm((f) => ({ ...f, faixas: f.faixas.filter((_, idx) => idx !== i).map((x, idx) => ({ ...x, ordem: idx + 1 })) }));

  const salvar = async () => {
    if (!editType || !editForm) return;
    if (!editForm.vigencia_inicio) return toast.error('Informe a vigência (início)');
    const slug = LEG_SLUG[editType];
    const payload = { ...editForm };
    delete payload.id; delete payload.criado_em; delete payload.criado_por; delete payload._id;
    setSaving(true);
    try {
      if (editMode === 'edit' && data[editType]?.id) {
        await axios.put(`${API}/api/calculos-dp/legislacao/${slug}/${data[editType].id}`, payload, authHeader());
        toast.success('Tabela atualizada com sucesso!');
      } else {
        await axios.post(`${API}/api/calculos-dp/legislacao/${slug}`, payload, authHeader());
        toast.success('Nova versão salva! A versão anterior foi arquivada no histórico.');
      }
      setEditType(null); setEditForm(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const numField = ({ label, k, step = '0.01', hint }) => (
    <div key={k}>
      <Label className="text-xs">{label}</Label>
      <Input type="number" step={step} value={editForm?.[k] ?? 0} onChange={(e) => upd({ [k]: parseFloat(e.target.value) || 0 })} />
      {hint && <p className="text-[10px] text-slate-400">{hint}</p>}
    </div>
  );

  if (loading) return <div className="p-6 text-slate-500">Carregando...</div>;
  if (!data) return null;

  // ---- Status de atualização das tabelas (aviso de tabela vencida + lembrete de início de ano) ----
  const hoje = new Date();
  const anoAtual = hoje.getFullYear();
  const temporadaRevisao = hoje.getMonth() <= 1; // janeiro (0) ou fevereiro (1)
  const TABELAS_ANUAIS = { inss: 'INSS', irrf: 'IRRF', salario_minimo: 'Salário Mínimo', salario_familia: 'Salário Família' };
  const LABELS_TABELA = { ...TABELAS_ANUAIS, fgts: 'FGTS' };
  const anoVigencia = (t) => {
    const v = data[t]?.vigencia_inicio;
    const a = v ? parseInt(String(v).slice(0, 4), 10) : null;
    return Number.isNaN(a) ? null : a;
  };
  const tabelaDesatualizada = (t) => {
    if (!data[t]) return true; // ausente
    if (TABELAS_ANUAIS[t]) { const a = anoVigencia(t); return a == null || a < anoAtual; } // vigência inválida/ausente também exige revisão
    return false;
  };
  const pendencias = Object.keys(LABELS_TABELA).filter((t) => tabelaDesatualizada(t));

  const Block = ({ Icon, title, vigencia, fonte, children, testid, tipo, desatualizada, mostrarAtivo }) => (
    <Card data-testid={testid} className={desatualizada ? 'border-amber-300' : ''}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-base flex-wrap">
              <Icon className="h-4 w-4 text-indigo-600" />
              {title}
              {mostrarAtivo && (
                <Badge className="text-xs bg-emerald-100 text-emerald-700 border border-emerald-300">
                  <CheckCircle2 className="h-3 w-3 mr-0.5 inline" /> Ativo
                </Badge>
              )}
              {vigencia && (
                <Badge variant="outline" className="ml-2 text-xs">
                  Vigência: {fmtDate(vigencia)}
                </Badge>
              )}
              {desatualizada && (
                <Badge className="text-xs bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-100" data-testid={`dp-leg-badge-desatualizada-${tipo}`}>
                  Desatualizada — revisar
                </Badge>
              )}
            </CardTitle>
            {fonte && <CardDescription className="text-xs mt-1">{fonte}</CardDescription>}
          </div>
          <div className="flex items-center gap-2">
            {tipo && (
              <>
                <Button size="sm" variant="outline" onClick={() => abrirEdicao(tipo, 'edit')} data-testid={`dp-leg-editar-${tipo}`}>
                  <Pencil className="h-3 w-3 mr-1" /> Editar
                </Button>
                <Button size="sm" variant="outline" onClick={() => abrirEdicao(tipo, 'new')} data-testid={`dp-leg-novo-${tipo}`}>
                  <Plus className="h-3 w-3 mr-1" /> Novo
                </Button>
              </>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="text-sm">{children}</CardContent>
    </Card>
  );

  const CardFaltante = ({ tipo, label }) => (
    <Card>
      <CardContent className="py-4 flex items-center justify-between">
        <span className="text-amber-700 text-sm">⚠️ Sem tabela {label} vigente.</span>
        <Button size="sm" variant="outline" onClick={() => abrirEdicao(tipo, 'new')} data-testid={`dp-leg-add-${tipo}`}>
          <Plus className="h-3 w-3 mr-1" /> Cadastrar
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-4">
      {temporadaRevisao && (
        <Card className="border-indigo-300 bg-indigo-50" data-testid="dp-leg-banner-temporada">
          <CardContent className="py-3 flex items-start gap-3">
            <CalendarClock className="h-5 w-5 text-indigo-600 mt-0.5 shrink-0" />
            <div className="text-sm text-indigo-900">
              <strong>Início de {anoAtual}:</strong> época de atualização das tabelas de encargos. Confira INSS, IRRF, salário mínimo e salário família do novo exercício e atualize aqui o que mudou.
            </div>
          </CardContent>
        </Card>
      )}
      {pendencias.length > 0 ? (
        <Card className="border-amber-300 bg-amber-50" data-testid="dp-leg-banner-pendencias">
          <CardContent className="py-3 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
            <div className="text-sm text-amber-900">
              <strong>{pendencias.length} tabela(s) precisam de revisão:</strong>{' '}{pendencias.map((t) => LABELS_TABELA[t]).join(', ')}.
              <span className="block text-xs text-amber-700 mt-1">
                Tabelas ausentes ou com vigência de anos anteriores. Clique em “Novo” (ou “Cadastrar”) na tabela correspondente para criar uma nova versão.
              </span>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-emerald-300 bg-emerald-50" data-testid="dp-leg-banner-ok">
          <CardContent className="py-3 flex items-center gap-3">
            <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
            <div className="text-sm text-emerald-900">Todas as tabelas de encargos estão atualizadas para {anoAtual}.</div>
          </CardContent>
        </Card>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => { loadHistoricoUnificado(); setHistoricoUnificadoOpen(true); }} data-testid="dp-leg-historico-completo-btn">
          <History className="h-4 w-4 mr-2" /> Histórico de Versões
        </Button>
        <Button variant="outline" onClick={load} data-testid="dp-legislacao-refresh-btn">
          <RefreshCw className="h-4 w-4 mr-2" /> Atualizar
        </Button>
      </div>
      {data.inss ? (
        <Block
          Icon={Landmark}
          title={data.inss.descricao}
          vigencia={data.inss.vigencia_inicio}
          fonte={data.inss.fonte_legal}
          testid="dp-leg-inss"
          tipo="inss"
          desatualizada={tabelaDesatualizada('inss')}
        // mostrarAtivo={data.inss.ativo}
        >
          <table className="w-full text-left">
            <thead>
              <tr className="text-xs uppercase text-slate-500 border-b">
                <th className="py-2">Faixa</th>
                <th>De</th>
                <th>Até</th>
                <th className="text-right">Alíquota</th>
              </tr>
            </thead>
            <tbody>
              {data.inss.faixas.map((f) => (
                <tr key={f.ordem} className="border-b last:border-0">
                  <td className="py-2">{f.ordem}ª</td>
                  <td>{fmtBRL(f.valor_inicio)}</td>
                  <td>{fmtBRL(f.valor_fim)}</td>
                  <td className="text-right font-medium">{fmtPct(f.aliquota)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-2 text-xs text-slate-600">
            Teto: <strong>{fmtBRL(data.inss.teto)}</strong> · Alíquota teto: <strong>{fmtPct(data.inss.aliquota_teto)}</strong>
          </div>
        </Block>
      ) : (
        <CardFaltante tipo="inss" label="INSS" />
      )}

      {data.irrf ? (
        <Block
          Icon={Scale}
          title={data.irrf.descricao}
          vigencia={data.irrf.vigencia_inicio}
          fonte={data.irrf.fonte_legal}
          testid="dp-leg-irrf"
          tipo="irrf"
          desatualizada={tabelaDesatualizada('irrf')}
        // mostrarAtivo={data.irrf.ativo}
        >
          <table className="w-full text-left">
            <thead>
              <tr className="text-xs uppercase text-slate-500 border-b">
                <th className="py-2">Faixa</th>
                <th>De</th>
                <th>Até</th>
                <th className="text-right">Alíq.</th>
                <th className="text-right">Dedução</th>
              </tr>
            </thead>
            <tbody>
              {data.irrf.faixas.map((f) => (
                <tr key={f.ordem} className="border-b last:border-0">
                  <td className="py-2">{f.ordem}ª</td>
                  <td>{fmtBRL(f.valor_inicio)}</td>
                  <td>{f.valor_fim >= 999999 ? '∞' : fmtBRL(f.valor_fim)}</td>
                  <td className="text-right">{fmtPct(f.aliquota)}</td>
                  <td className="text-right">{fmtBRL(f.deducao)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-2 text-xs text-slate-600 space-x-3">
            <span>Dep.: <strong>{fmtBRL(data.irrf.deducao_por_dependente)}</strong></span>
            <span>Desc. simplificado: <strong>{fmtBRL(data.irrf.desconto_simplificado_mensal)}</strong></span>
            <span>Isenção aposentado 65+: <strong>{fmtBRL(data.irrf.limite_isencao_aposentado_65)}</strong></span>
          </div>
        </Block>
      ) : (
        <CardFaltante tipo="irrf" label="IRRF" />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {data.fgts ? (
          <Block Icon={Wallet} title={data.fgts.descricao} vigencia={data.fgts.vigencia_inicio} fonte={data.fgts.fonte_legal} testid="dp-leg-fgts" tipo="fgts" desatualizada={tabelaDesatualizada('fgts')} /*mostrarAtivo={data.fgts.ativo}*/>
            <ul className="space-y-1 text-sm">
              <li>Alíquota normal: <strong>{fmtPct(data.fgts.aliquota_normal)}</strong></li>
              <li>Aprendiz: <strong>{fmtPct(data.fgts.aliquota_aprendiz)}</strong></li>
              <li>Doméstico: <strong>{fmtPct(data.fgts.aliquota_domestico)}</strong></li>
              <li>Multa s/ justa causa: <strong>{fmtPct(data.fgts.multa_rescisoria_sem_justa_causa)}</strong></li>
              <li>Multa acordo: <strong>{fmtPct(data.fgts.multa_rescisoria_acordo)}</strong></li>
            </ul>
          </Block>
        ) : (
          <CardFaltante tipo="fgts" label="FGTS" />
        )}
        {data.salario_minimo ? (
          <Block Icon={Coins} title={data.salario_minimo.descricao} vigencia={data.salario_minimo.vigencia_inicio} fonte={data.salario_minimo.fonte_legal} testid="dp-leg-sm" tipo="salario_minimo" desatualizada={tabelaDesatualizada('salario_minimo')} /*mostrarAtivo={data.salario_minimo.ativo}*/>
            <div className="text-2xl font-bold text-indigo-700">{fmtBRL(data.salario_minimo.valor_nacional)}</div>
            <div className="text-xs text-slate-500">valor nacional</div>
            {data.salario_minimo.valores_estaduais && (
              <div className="mt-2 grid grid-cols-2 gap-1 text-xs">
                {Object.entries(data.salario_minimo.valores_estaduais).map(([uf, v]) => (
                  <div key={uf}><span className="font-medium">{uf}:</span> {fmtBRL(v)}</div>
                ))}
              </div>
            )}
          </Block>
        ) : (
          <CardFaltante tipo="salario_minimo" label="Salário Mínimo" />
        )}
        {data.salario_familia ? (
          <Block Icon={Users2} title={data.salario_familia.descricao} vigencia={data.salario_familia.vigencia_inicio} fonte={data.salario_familia.fonte_legal} testid="dp-leg-sf" tipo="salario_familia" desatualizada={tabelaDesatualizada('salario_familia')} /*mostrarAtivo={data.salario_familia.ativo}*/>
            {data.salario_familia.faixas.map((f) => (
              <div key={f.ordem} className="text-sm">
                Remuneração até {fmtBRL(f.valor_max_remuneracao)} → <strong>{fmtBRL(f.valor_cota_por_filho)}</strong>/filho &lt; {data.salario_familia.idade_maxima_filho} anos
              </div>
            ))}
          </Block>
        ) : (
          <CardFaltante tipo="salario_familia" label="Salário Família" />
        )}
        {data.vt_limite ? (
          <Block Icon={Bus} title={data.vt_limite.descricao} vigencia={data.vt_limite.vigencia_inicio} fonte={data.vt_limite.fonte_legal} testid="dp-leg-vt" tipo="vt_limite">
            <div className="text-sm">Limite máximo de desconto do salário-base: <strong>{fmtPct(data.vt_limite.percentual_maximo_desconto)}</strong></div>
          </Block>
        ) : (
          <CardFaltante tipo="vt_limite" label="VT Limite" />
        )}
        {data.encargos_patronais ? (
          <Block Icon={Building2} title={data.encargos_patronais.descricao} vigencia={data.encargos_patronais.vigencia_inicio} fonte={data.encargos_patronais.fonte_legal} testid="dp-leg-encargos" tipo="encargos_patronais">
            <ul className="space-y-1 text-sm">
              <li>INSS Patronal: <strong>{fmtPct(data.encargos_patronais.inss_patronal_aliquota)}</strong></li>
              <li>RAT: <strong>{fmtPct(data.encargos_patronais.rat_aliquota)}</strong></li>
              <li>FAP: <strong>{data.encargos_patronais.fap_padrao}</strong></li>
              <li>Terceiros: <strong>{fmtPct(data.encargos_patronais.terceiros_aliquota)}</strong></li>
            </ul>
          </Block>
        ) : (
          <CardFaltante tipo="encargos_patronais" label="Encargos Patronais" />
        )}
      </div>



      <Dialog open={historicoUnificadoOpen} onOpenChange={(o) => { if (!o) setHistoricoUnificadoOpen(false); }}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="dp-leg-historico-completo-dialog">
          <DialogHeader>
            <DialogTitle>Histórico de Versões — Legislação Vigente</DialogTitle>
            <DialogDescription>
              Consulte versões anteriores de todas as tabelas de legislação. Visualização exclusiva de consulta e auditoria.
            </DialogDescription>
          </DialogHeader>
          {loadingHistoricoUnificado ? (
            <div className="py-8 text-center text-slate-500">Carregando...</div>
          ) : historicoUnificado.length === 0 ? (
            <div className="py-8 text-center text-slate-500">Nenhuma versão cadastrada.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="text-xs uppercase text-slate-500 border-b">
                    <th className="py-2 pr-2">Nome da Versão</th>
                    <th className="py-2 pr-2">Tipo</th>
                    <th className="py-2 pr-2">Data de Criação</th>
                    <th className="py-2 pr-2">Criado por</th>
                    <th className="py-2 pr-2">Observações</th>
                    <th className="py-2 pr-2 text-right">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {historicoUnificado.map((v) => (
                    <tr key={`${v.tipo}-${v.id}`} className="border-b last:border-0">
                      <td className="py-2 pr-2 font-medium">{v.descricao}</td>
                      <td className="py-2 pr-2">
                        <Badge variant="outline" className="text-xs">{v.tipo_label}</Badge>
                      </td>
                      <td className="py-2 pr-2 text-slate-600">{fmtDate(v.criado_em)}</td>
                      <td className="py-2 pr-2 text-slate-600">{v.criado_por || '—'}</td>
                      <td className="py-2 pr-2 text-slate-500 max-w-[200px] truncate">{v.observacoes || '—'}</td>
                      <td className="py-2 pr-2 text-right">
                        <Button size="sm" variant="outline" onClick={() => setVisualizarVersao(v)}>
                          <Eye className="h-3.5 w-3.5 mr-1" /> Visualizar
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoricoUnificadoOpen(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!visualizarVersao} onOpenChange={(o) => { if (!o) setVisualizarVersao(null); }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="dp-leg-visualizar-dialog">
          <DialogHeader>
            <DialogTitle>{visualizarVersao?.descricao || 'Versão'}</DialogTitle>
            <DialogDescription>
              {visualizarVersao?.tipo_label} · Vigência: {fmtDate(visualizarVersao?.vigencia_inicio)}
              {visualizarVersao?.fonte_legal ? ` · ${visualizarVersao.fonte_legal}` : ''}
            </DialogDescription>
          </DialogHeader>
          {visualizarVersao && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><span className="text-slate-500 text-xs">Vigência início:</span> <span className="font-medium">{fmtDate(visualizarVersao.vigencia_inicio)}</span></div>
                <div><span className="text-slate-500 text-xs">Vigência fim:</span> <span className="font-medium">{fmtDate(visualizarVersao.vigencia_fim)}</span></div>
                <div><span className="text-slate-500 text-xs">Criado em:</span> <span className="font-medium">{fmtDate(visualizarVersao.criado_em)}</span></div>
                <div><span className="text-slate-500 text-xs">Criado por:</span> <span className="font-medium">{visualizarVersao.criado_por || '—'}</span></div>
                <div className="col-span-2"><span className="text-slate-500 text-xs">Fonte legal:</span> <span className="font-medium">{visualizarVersao.fonte_legal || '—'}</span></div>
                {visualizarVersao.observacoes && (
                  <div className="col-span-2"><span className="text-slate-500 text-xs">Observações:</span> <span className="font-medium">{visualizarVersao.observacoes}</span></div>
                )}
              </div>

              {visualizarVersao.tipo === 'inss' && (
                <>
                  <div className="border rounded-md p-3">
                    <h4 className="text-xs uppercase text-slate-500 mb-2">Faixas</h4>
                    <table className="w-full text-left">
                      <thead><tr className="text-xs text-slate-500 border-b"><th className="py-1 pr-2">Faixa</th><th className="py-1 pr-2">De</th><th className="py-1 pr-2">Até</th><th className="py-1 pr-2 text-right">Alíquota</th></tr></thead>
                      <tbody>
                        {(visualizarVersao.faixas || []).map((f) => (
                          <tr key={f.ordem} className="border-b last:border-0">
                            <td className="py-1 pr-2">{f.ordem}ª</td>
                            <td className="py-1 pr-2">{fmtBRL(f.valor_inicio)}</td>
                            <td className="py-1 pr-2">{fmtBRL(f.valor_fim)}</td>
                            <td className="py-1 pr-2 text-right font-medium">{fmtPct(f.aliquota)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="text-xs text-slate-600">
                    Teto: <strong>{fmtBRL(visualizarVersao.teto)}</strong> · Alíquota teto: <strong>{fmtPct(visualizarVersao.aliquota_teto)}</strong>
                  </div>
                </>
              )}

              {visualizarVersao.tipo === 'irrf' && (
                <>
                  <div className="border rounded-md p-3">
                    <h4 className="text-xs uppercase text-slate-500 mb-2">Faixas</h4>
                    <table className="w-full text-left">
                      <thead><tr className="text-xs text-slate-500 border-b"><th className="py-1 pr-2">Faixa</th><th className="py-1 pr-2">De</th><th className="py-1 pr-2">Até</th><th className="py-1 pr-2 text-right">Alíq.</th><th className="py-1 pr-2 text-right">Dedução</th></tr></thead>
                      <tbody>
                        {(visualizarVersao.faixas || []).map((f) => (
                          <tr key={f.ordem} className="border-b last:border-0">
                            <td className="py-1 pr-2">{f.ordem}ª</td>
                            <td className="py-1 pr-2">{fmtBRL(f.valor_inicio)}</td>
                            <td className="py-1 pr-2">{f.valor_fim >= 999999 ? '∞' : fmtBRL(f.valor_fim)}</td>
                            <td className="py-1 pr-2 text-right">{fmtPct(f.aliquota)}</td>
                            <td className="py-1 pr-2 text-right">{fmtBRL(f.deducao)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="text-xs text-slate-600 space-x-3">
                    <span>Dep.: <strong>{fmtBRL(visualizarVersao.deducao_por_dependente)}</strong></span>
                    <span>Desc. simplificado: <strong>{fmtBRL(visualizarVersao.desconto_simplificado_mensal)}</strong></span>
                    <span>Isenção aposentado 65+: <strong>{fmtBRL(visualizarVersao.limite_isencao_aposentado_65)}</strong></span>
                  </div>
                </>
              )}

              {visualizarVersao.tipo === 'fgts' && (
                <ul className="space-y-1 text-sm border rounded-md p-3">
                  <li>Alíquota normal: <strong>{fmtPct(visualizarVersao.aliquota_normal)}</strong></li>
                  <li>Aprendiz: <strong>{fmtPct(visualizarVersao.aliquota_aprendiz)}</strong></li>
                  <li>Doméstico: <strong>{fmtPct(visualizarVersao.aliquota_domestico)}</strong></li>
                  <li>Multa s/ justa causa: <strong>{fmtPct(visualizarVersao.multa_rescisoria_sem_justa_causa)}</strong></li>
                  <li>Multa acordo: <strong>{fmtPct(visualizarVersao.multa_rescisoria_acordo)}</strong></li>
                </ul>
              )}

              {visualizarVersao.tipo === 'salario_minimo' && (
                <div className="border rounded-md p-3">
                  <div className="text-2xl font-bold text-indigo-700">{fmtBRL(visualizarVersao.valor_nacional)}</div>
                  <div className="text-xs text-slate-500">valor nacional</div>
                  {visualizarVersao.valores_estaduais && (
                    <div className="mt-2 grid grid-cols-2 gap-1 text-xs">
                      {Object.entries(visualizarVersao.valores_estaduais).map(([uf, val]) => (
                        <div key={uf}><span className="font-medium">{uf}:</span> {fmtBRL(val)}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {visualizarVersao.tipo === 'salario_familia' && (
                <div className="border rounded-md p-3">
                  <h4 className="text-xs uppercase text-slate-500 mb-2">Faixas</h4>
                  {(visualizarVersao.faixas || []).map((f) => (
                    <div key={f.ordem} className="text-sm">
                      Remuneração até {fmtBRL(f.valor_max_remuneracao)} → <strong>{fmtBRL(f.valor_cota_por_filho)}</strong>/filho &lt; {visualizarVersao.idade_maxima_filho} anos
                    </div>
                  ))}
                </div>
              )}

              {visualizarVersao.tipo === 'vt_limite' && (
                <div className="border rounded-md p-3">
                  Limite máximo de desconto do salário-base: <strong>{fmtPct(visualizarVersao.percentual_maximo_desconto)}</strong>
                </div>
              )}

              {visualizarVersao.tipo === 'encargos_patronais' && (
                <div className="border rounded-md p-3">
                  <ul className="space-y-1 text-sm">
                    <li>INSS Patronal: <strong>{fmtPct(visualizarVersao.inss_patronal_aliquota)}</strong></li>
                    <li>RAT: <strong>{fmtPct(visualizarVersao.rat_aliquota)}</strong></li>
                    <li>FAP: <strong>{visualizarVersao.fap_padrao}</strong></li>
                    <li>Terceiros: <strong>{fmtPct(visualizarVersao.terceiros_aliquota)}</strong></li>
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setVisualizarVersao(null)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editType} onOpenChange={(o) => { if (!o) { setEditType(null); setEditForm(null); } }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="dp-leg-edit-dialog">
          <DialogHeader>
            <DialogTitle>
              {editMode === 'edit' ? 'Editar' : 'Nova Versão'} — {editType === 'inss' ? 'Tabela INSS' : editType === 'irrf' ? 'Tabela IRRF' : editType === 'fgts' ? 'FGTS' : editType === 'salario_minimo' ? 'Salário Mínimo' : editType === 'salario_familia' ? 'Salário Família' : editType === 'vt_limite' ? 'VT — Vale Transporte' : 'Encargos Patronais'}
            </DialogTitle>
            <DialogDescription>Alíquotas em formato decimal (ex.: 0,075 = 7,5%). Valores em reais.</DialogDescription>
          </DialogHeader>
          {editForm && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label className="text-xs">Descrição</Label><Input value={editForm.descricao || ''} onChange={(e) => upd({ descricao: e.target.value })} /></div>
                <div><Label className="text-xs">Vigência início *</Label><Input type="date" value={editForm.vigencia_inicio || ''} onChange={(e) => upd({ vigencia_inicio: e.target.value })} /></div>
                <div><Label className="text-xs">Vigência fim</Label><Input type="date" value={editForm.vigencia_fim || ''} onChange={(e) => upd({ vigencia_fim: e.target.value || null })} /></div>
                <div className="col-span-2"><Label className="text-xs">Fonte legal</Label><Input value={editForm.fonte_legal || ''} onChange={(e) => upd({ fonte_legal: e.target.value })} placeholder="Ex.: Portaria MPS nº 6/2025" /></div>
                <div className="col-span-2"><Label className="text-xs">Observações</Label><Textarea value={editForm.observacoes || ''} onChange={(e) => upd({ observacoes: e.target.value })} placeholder="Observações sobre esta versão (opcional)" rows={2} /></div>
              </div>

              {(editType === 'inss' || editType === 'irrf' || editType === 'salario_familia') && (
                <div className="border rounded-md p-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm font-semibold">Faixas</Label>
                    <Button size="sm" variant="outline" onClick={addFaixa} data-testid="dp-leg-add-faixa"><Plus className="h-3 w-3 mr-1" />Faixa</Button>
                  </div>
                  <div className="space-y-2">
                    {(editForm.faixas || []).map((f, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-1"><Label className="text-[10px]">Ordem</Label><Input value={f.ordem} readOnly className="bg-slate-50" /></div>
                        {editType === 'salario_familia' ? (
                          <>
                            <div className="col-span-5"><Label className="text-[10px]">Remuneração até (R$)</Label><Input type="number" step="0.01" value={f.valor_max_remuneracao} onChange={(e) => updFaixa(i, { valor_max_remuneracao: parseFloat(e.target.value) || 0 })} /></div>
                            <div className="col-span-5"><Label className="text-[10px]">Cota por filho (R$)</Label><Input type="number" step="0.01" value={f.valor_cota_por_filho} onChange={(e) => updFaixa(i, { valor_cota_por_filho: parseFloat(e.target.value) || 0 })} /></div>
                          </>
                        ) : (
                          <>
                            <div className="col-span-3"><Label className="text-[10px]">De (R$)</Label><Input type="number" step="0.01" value={f.valor_inicio} onChange={(e) => updFaixa(i, { valor_inicio: parseFloat(e.target.value) || 0 })} /></div>
                            <div className="col-span-3"><Label className="text-[10px]">Até (R$)</Label><Input type="number" step="0.01" value={f.valor_fim} onChange={(e) => updFaixa(i, { valor_fim: parseFloat(e.target.value) || 0 })} /></div>
                            <div className="col-span-2"><Label className="text-[10px]">Alíquota</Label><Input type="number" step="0.0001" value={f.aliquota} onChange={(e) => updFaixa(i, { aliquota: parseFloat(e.target.value) || 0 })} /></div>
                            {editType === 'irrf' && (
                              <div className="col-span-2"><Label className="text-[10px]">Dedução (R$)</Label><Input type="number" step="0.01" value={f.deducao} onChange={(e) => updFaixa(i, { deducao: parseFloat(e.target.value) || 0 })} /></div>
                            )}
                          </>
                        )}
                        <div className="col-span-1"><Button size="sm" variant="ghost" onClick={() => delFaixa(i)}><Trash2 className="h-4 w-4 text-red-600" /></Button></div>
                      </div>
                    ))}
                    {(editForm.faixas || []).length === 0 && <p className="text-xs text-slate-500">Nenhuma faixa. Clique em "Faixa" para adicionar.</p>}
                  </div>
                </div>
              )}

              {editType === 'inss' && (
                <div className="grid grid-cols-2 gap-3">
                  {numField({ label: 'Teto (R$)', k: 'teto' })}
                  {numField({ label: 'Alíquota no teto', k: 'aliquota_teto', step: '0.0001', hint: '0,14 = 14%' })}
                </div>
              )}

              {editType === 'irrf' && (
                <div className="grid grid-cols-3 gap-3">
                  {numField({ label: 'Dedução por dependente', k: 'deducao_por_dependente' })}
                  {numField({ label: 'Desconto simplificado', k: 'desconto_simplificado_mensal' })}
                  {numField({ label: 'Isenção aposentado 65+', k: 'limite_isencao_aposentado_65' })}
                  {numField({ label: 'Isenção total até (Lei 15.270/25)', k: 'isencao_total_ate' })}
                  {numField({ label: 'Redução parcial até', k: 'reducao_parcial_ate' })}
                  {numField({ label: 'Redução parcela fixa', k: 'reducao_parcela_fixa' })}
                  {numField({ label: 'Redução alíquota', k: 'reducao_aliquota', step: '0.000001', hint: '0,133145' })}
                </div>
              )}

              {editType === 'fgts' && (
                <div className="grid grid-cols-3 gap-3">
                  {numField({ label: 'Alíquota normal', k: 'aliquota_normal', step: '0.0001', hint: '0,08 = 8%' })}
                  {numField({ label: 'Alíquota aprendiz', k: 'aliquota_aprendiz', step: '0.0001' })}
                  {numField({ label: 'Alíquota doméstico', k: 'aliquota_domestico', step: '0.0001' })}
                  {numField({ label: 'Multa s/ justa causa', k: 'multa_rescisoria_sem_justa_causa', step: '0.0001', hint: '0,40 = 40%' })}
                  {numField({ label: 'Multa acordo', k: 'multa_rescisoria_acordo', step: '0.0001', hint: '0,20 = 20%' })}
                </div>
              )}

              {editType === 'salario_minimo' && (
                <div className="grid grid-cols-2 gap-3">
                  {numField({ label: 'Valor nacional (R$)', k: 'valor_nacional' })}
                </div>
              )}

              {editType === 'salario_familia' && (
                <div className="grid grid-cols-2 gap-3">
                  {numField({ label: 'Idade máxima do filho', k: 'idade_maxima_filho', step: '1' })}
                </div>
              )}

              {editType === 'vt_limite' && (
                <div className="grid grid-cols-2 gap-3">
                  {numField({ label: '% Máximo de desconto', k: 'percentual_maximo_desconto', step: '0.001', hint: '0,06 = 6%' })}
                </div>
              )}

              {editType === 'encargos_patronais' && (
                <div className="grid grid-cols-2 gap-3">
                  {numField({ label: 'INSS Patronal (decimal)', k: 'inss_patronal_aliquota', step: '0.001', hint: '0,20 = 20%' })}
                  {numField({ label: 'RAT (decimal)', k: 'rat_aliquota', step: '0.001', hint: '0,03 = 3%' })}
                  {numField({ label: 'FAP', k: 'fap_padrao', step: '0.01', hint: '1,0 = 100%' })}
                  {numField({ label: 'Terceiros (decimal)', k: 'terceiros_aliquota', step: '0.001', hint: '0,058 = 5,8%' })}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditType(null); setEditForm(null); }}>Cancelar</Button>
            <Button onClick={salvar} disabled={saving} data-testid="dp-leg-salvar-btn">{saving ? 'Salvando...' : 'Salvar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================================================
// TAB: SINDICATOS (CRUD)
// ============================================================================
export const SindicatosTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const empty = {
    nome: '', sigla: '', cnpj: '', codigo_fpas: '',
    tipo: 'laboral', base_territorial: '', categoria: '',
    cidade: '', uf: '', email: '', telefone: '',
    contribuicao_assistencial_percentual: 0,
    contribuicao_confederativa_percentual: 0,
    mensalidade_associado: 0,
    desconto_sindical_ativo: false,
    desconto_sindical_tipo: 'fixo',
    desconto_sindical_valor: 0,
    desconto_sindical_teto_valor: 0,
    desconto_sindical_faixa_base: 'salario_minimo',
    desconto_sindical_faixas: [],
    desconto_sindical_descricao: '',
    observacoes: '',
  };
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/calculos-dp/sindicatos`, authHeader());
      setItems(r.data || []);
    } catch (e) { toast.error('Erro ao listar sindicatos'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEdit(null); setForm(empty); setOpen(true); };

  const openEdit = (s) => {
    setEdit(s.id);
    const valorLegado = Number(s.mensalidade_associado || 0);
    const tipoRegra = s.desconto_sindical_tipo || (valorLegado > 0 ? 'fixo' : 'fixo');
    const ativoRegra = !!s.desconto_sindical_ativo || (!s.desconto_sindical_ativo && valorLegado > 0 && !s.desconto_sindical_tipo);
    setForm({
      ...empty,
      ...s,
      desconto_sindical_ativo: ativoRegra,
      desconto_sindical_tipo: tipoRegra,
      desconto_sindical_valor: s.desconto_sindical_valor || valorLegado || 0,
      desconto_sindical_teto_valor: s.desconto_sindical_teto_valor || 0,
      desconto_sindical_faixa_base: s.desconto_sindical_faixa_base || 'salario_minimo',
      desconto_sindical_faixas: normalizeDescontoSindicalFaixas(s.desconto_sindical_faixas),
      desconto_sindical_descricao: s.desconto_sindical_descricao || '',
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.nome) return toast.error('Nome é obrigatório');
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      return toast.error('E-mail inválido');
    }
    const erroFaixas = validarFaixasDescontoSindical(form);
    if (erroFaixas) return toast.error(erroFaixas);
    try {
      const payload = {
        ...form,
        ...buildDescontoSindicalPayload(form, { espelhaMensalidade: true }),
      };
      if (edit) {
        await axios.put(`${API}/api/calculos-dp/sindicatos/${edit}`, payload, authHeader());
        toast.success('Sindicato atualizado');
      } else {
        await axios.post(`${API}/api/calculos-dp/sindicatos`, payload, authHeader());
        toast.success('Sindicato criado');
      }
      setOpen(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao salvar sindicato');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Excluir este sindicato?')) return;
    try {
      await axios.delete(`${API}/api/calculos-dp/sindicatos/${id}`, authHeader());
      toast.success('Sindicato excluído'); load();
    } catch (e) { toast.error('Erro ao excluir'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-600">{items.length} sindicato(s) cadastrado(s).</p>
        <Button onClick={openCreate} data-testid="dp-sindicato-novo-btn">
          <Plus className="h-4 w-4 mr-2" /> Novo Sindicato
        </Button>
      </div>

      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600">
            <tr>
              <th className="text-left p-3">Nome</th>
              <th className="text-left p-3">Sigla</th>
              <th className="text-left p-3">CNPJ</th>
              <th className="text-left p-3">Tipo</th>
              <th className="text-left p-3">UF</th>
              <th className="text-right p-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="p-4 text-slate-500 text-center">Carregando...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="p-4 text-slate-500 text-center">Nenhum sindicato cadastrado.</td></tr>
            ) : items.map((s) => (
              <tr key={s.id} className="border-t hover:bg-slate-50">
                <td className="p-3 font-medium">{s.nome}</td>
                <td className="p-3">{s.sigla || '—'}</td>
                <td className="p-3">{s.cnpj || '—'}</td>
                <td className="p-3">
                  <Badge variant="outline">{s.tipo}</Badge>
                </td>
                <td className="p-3">{s.uf || '—'}</td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(s)} data-testid={`dp-sindicato-edit-${s.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(s.id)} data-testid={`dp-sindicato-delete-${s.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dp-sindicato-dialog">
          <DialogHeader>
            <DialogTitle>{edit ? 'Editar Sindicato' : 'Novo Sindicato'}</DialogTitle>
            <DialogDescription>Dados cadastrais do sindicato (laboral ou patronal) usados nas convenções coletivas e contribuições.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Nome *</Label>
              <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="dp-sindicato-nome" />
            </div>
            <div>
              <Label>Sigla</Label>
              <Input value={form.sigla} onChange={(e) => setForm({ ...form, sigla: e.target.value })} />
            </div>
            <div>
              <Label>CNPJ</Label>
              <Input value={form.cnpj} onChange={(e) => setForm({ ...form, cnpj: formatCNPJ(e.target.value.replace(/\D/g, '').slice(0, 14)) })} />
            </div>
            <div>
              <Label>Código FPAS</Label>
              <Input value={form.codigo_fpas} onChange={(e) => setForm({ ...form, codigo_fpas: e.target.value })} />
            </div>
            <div>
              <Label>Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="laboral">Laboral</SelectItem>
                  <SelectItem value="patronal">Patronal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Categoria</Label>
              <Input value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Base territorial</Label>
              <Input value={form.base_territorial} onChange={(e) => setForm({ ...form, base_territorial: e.target.value })} />
            </div>
            <div>
              <Label>Cidade</Label>
              <Input value={form.cidade} onChange={(e) => setForm({ ...form, cidade: e.target.value })} />
            </div>
            <div>
              <Label>UF</Label>
              <Input value={form.uf} maxLength={2} onChange={(e) => setForm({ ...form, uf: e.target.value.toUpperCase() })} />
            </div>
            <div>
              <Label>E-mail</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label>Telefone</Label>
              <Input value={form.telefone} onChange={(e) => setForm({ ...form, telefone: formatPhone(e.target.value) })} />
            </div>
            <div>
              <Label>Contrib. Assistencial (%)</Label>
              <PercentInput value={form.contribuicao_assistencial_percentual} onChange={(v) => setForm({ ...form, contribuicao_assistencial_percentual: v })} />
            </div>
            <div>
              <Label>Contrib. Confederativa (%)</Label>
              <PercentInput value={form.contribuicao_confederativa_percentual} onChange={(v) => setForm({ ...form, contribuicao_confederativa_percentual: v })} />
            </div>
            <div className="col-span-2 rounded-md border p-4 space-y-4 bg-slate-50">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label className="text-sm font-medium">Regra Padrão de Desconto Sindical</Label>
                  <p className="text-xs text-slate-500">
                    Usada automaticamente pela folha quando a convenção coletiva não sobrescrever esta regra.
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={!!form.desconto_sindical_ativo}
                  onChange={(e) => setForm({ ...form, desconto_sindical_ativo: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300"
                />
              </div>

              {form.desconto_sindical_ativo && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Tipo</Label>
                    <Select value={form.desconto_sindical_tipo || 'fixo'} onValueChange={(v) => setForm({ ...form, desconto_sindical_tipo: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fixo">Valor fixo</SelectItem>
                        <SelectItem value="percentual">Percentual sobre salário base</SelectItem>
                        <SelectItem value="faixa">Por faixa salarial</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {form.desconto_sindical_tipo !== 'faixa' && (
                    <div>
                      <Label>{form.desconto_sindical_tipo === 'percentual' ? 'Percentual (%)' : 'Valor mensal (R$)'}</Label>
                      {form.desconto_sindical_tipo === 'percentual' ? (
                        <PercentInput value={form.desconto_sindical_valor} onChange={(v) => setForm({ ...form, desconto_sindical_valor: v })} />
                      ) : (
                        <CurrencyInput value={form.desconto_sindical_valor} onChange={(v) => setForm({ ...form, desconto_sindical_valor: v })} />
                      )}
                    </div>
                  )}
                  {form.desconto_sindical_tipo === 'percentual' && (
                    <div>
                      <Label>Teto máximo do desconto (R$)</Label>
                      <CurrencyInput value={form.desconto_sindical_teto_valor} onChange={(v) => setForm({ ...form, desconto_sindical_teto_valor: v })} />
                    </div>
                  )}
                  {form.desconto_sindical_tipo === 'faixa' && (
                    <DescontoSindicalFaixasEditor form={form} setForm={setForm} />
                  )}
                  <div className={form.desconto_sindical_tipo === 'percentual' ? '' : 'col-span-2'}>
                    <Label>Descrição complementar</Label>
                    <Input value={form.desconto_sindical_descricao || ''} onChange={(e) => setForm({ ...form, desconto_sindical_descricao: e.target.value })} placeholder="Ex.: Mensalidade sindical padrão" />
                  </div>
                </div>
              )}
            </div>
            <div className="col-span-2">
              <Label>Observações</Label>
              <Textarea value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} data-testid="dp-sindicato-salvar-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================================================
// TAB: CONVENÇÕES COLETIVAS (CCT)
// ============================================================================
export const ConvencoesTab = () => {
  const navigate = useNavigate();
  const { confirm, ConfirmDialog } = useConfirm();
  const [items, setItems] = useState([]);
  const [sindicatos, setSindicatos] = useState([]);
  const [rubricasDesconto, setRubricasDesconto] = useState([]);
  const [rubricasHoraExtra, setRubricasHoraExtra] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const empty = {
    nome: '', numero_registro_mte: '',
    sindicato_laboral_id: '', sindicato_patronal_id: '',
    vigencia_inicio: '', vigencia_fim: '',
    data_base: '', abrangencia: '',
    piso_salarial_geral: 0, reajuste_anual_percentual: 0,
    desconto_sindical_ativo: false,
    desconto_sindical_tipo: 'fixo',
    desconto_sindical_valor: 0,
    desconto_sindical_teto_valor: 0,
    desconto_sindical_faixa_base: 'salario_minimo',
    desconto_sindical_faixas: [],
    desconto_sindical_descricao: '',
    rubrica_desconto_sindical_id: '',
    rubrica_hora_extra_semana_id: '',
    rubrica_hora_extra_fim_semana_id: '',
    clausulas: [],
    observacoes: '',
  };
  const [form, setForm] = useState(empty);
  const [dissidioOpen, setDissidioOpen] = useState(false);
  const [dissidioSaving, setDissidioSaving] = useState(false);
  const [dissidioResult, setDissidioResult] = useState(null);
  const [dissidioForm, setDissidioForm] = useState({
    convencao_id: '',
    convencao_nome: '',
    data_evento: new Date().toISOString().slice(0, 10),
    percentual: 0,
    aplicar_piso_salarial: true,
    incluir_retroativo: false,
    competencia_pagamento_retroativo: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rCct, rSind, rRubricas] = await Promise.all([
        axios.get(`${API}/api/calculos-dp/convencoes`, authHeader()),
        axios.get(`${API}/api/calculos-dp/sindicatos`, authHeader()),
        axios.get(`${API}/api/calculos-dp/rubricas?ativo=true`, authHeader()),
      ]);
      setItems(rCct.data || []);
      setSindicatos(rSind.data || []);
      const rubricas = rRubricas.data || [];
      setRubricasDesconto(rubricas.filter((rubrica) => rubrica.tipo === 'desconto'));
      setRubricasHoraExtra(
        rubricas
          .filter((rubrica) => rubrica.tipo === 'provento' && isHoraExtraFormula(rubrica.formula_tipo))
          .sort((a, b) => (percentualHoraExtra(a.formula_tipo) || 0) - (percentualHoraExtra(b.formula_tipo) || 0))
      );
    } catch (e) { toast.error('Erro ao listar convenções'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEdit(null);
    // Set default date to today
    const todayDefault = new Date();
    const todayStr = `${String(todayDefault.getDate()).padStart(2, '0')}/${String(todayDefault.getMonth() + 1).padStart(2, '0')}/${todayDefault.getFullYear()}`;
    setForm({
      ...empty,
      vigencia_inicio: todayStr,
      vigencia_fim: todayStr,
    });
    setOpen(true);
  };
  const openEdit = (c) => {
    setEdit(c.id);
    setForm({
      ...empty,
      ...c,
      piso_salarial_geral: c.piso_salarial_geral || 0,
      reajuste_anual_percentual: c.reajuste_anual_percentual || 0,
      desconto_sindical_ativo: !!c.desconto_sindical_ativo,
      desconto_sindical_tipo: c.desconto_sindical_tipo || 'fixo',
      desconto_sindical_valor: c.desconto_sindical_valor || 0,
      desconto_sindical_teto_valor: c.desconto_sindical_teto_valor || 0,
      desconto_sindical_faixa_base: c.desconto_sindical_faixa_base || 'salario_minimo',
      desconto_sindical_faixas: normalizeDescontoSindicalFaixas(c.desconto_sindical_faixas),
      desconto_sindical_descricao: c.desconto_sindical_descricao || '',
      rubrica_desconto_sindical_id: c.rubrica_desconto_sindical_id || '',
      rubrica_hora_extra_semana_id: c.rubrica_hora_extra_semana_id || '',
      rubrica_hora_extra_fim_semana_id: c.rubrica_hora_extra_fim_semana_id || '',
      clausulas: c.clausulas || [],
    });
    setOpen(true);
  };

  const openDissidio = (convencaoBase) => {
    const origem = convencaoBase || form;
    setDissidioResult(null);
    setDissidioForm({
      convencao_id: origem.id || edit || '',
      convencao_nome: origem.nome || '',
      data_evento: new Date().toISOString().slice(0, 10),
      percentual: Number(origem.reajuste_anual_percentual || 0),
      aplicar_piso_salarial: true,
      incluir_retroativo: false,
      competencia_pagamento_retroativo: '',
    });
    setDissidioOpen(true);
  };

  const save = async () => {
    if (!form.nome || !form.sindicato_laboral_id || !form.vigencia_inicio || !form.vigencia_fim) {
      return toast.error('Nome, sindicato laboral e vigências são obrigatórios');
    }

    if (!/[a-zA-Z0-9\u00C0-\u024F]/.test(form.nome)) {
      return toast.error('Nome da Convenção deve conter ao menos uma letra ou número');
    }

    // Validar formato das vigências (DD/MM/YYYY ou apenas ano)
    const validateVigencia = (vigencia) => {
      if (!vigencia) return false;
      // Se for apenas 4 dígitos, deve ser um ano válido (apenas números)
      if (vigencia.length === 4) {
        return /^\d{4}$/.test(vigencia);
      }
      // Se for formato DD/MM/YYYY
      return /^\d{2}\/\d{2}\/\d{4}$/.test(vigencia);
    };

    if (!validateVigencia(form.vigencia_inicio)) {
      return toast.error('Vigência Início deve ser no formato DD/MM/YYYY (ex: 01/01/2026) ou apenas o ano (AAAA)');
    }

    if (!validateVigencia(form.vigencia_fim)) {
      return toast.error('Vigência Fim deve ser no formato DD/MM/YYYY (ex: 31/12/2026) ou apenas o ano (AAAA)');
    }

    // Validações adicionais de ordem lógica
    if (form.vigencia_inicio.length === 4 && form.vigencia_fim.length === 4) {
      const anoInicio = parseInt(form.vigencia_inicio);
      const anoFim = parseInt(form.vigencia_fim);
      if (anoInicio > anoFim) {
        return toast.error('Vigência Início não pode ser maior que Vigência Fim');
      }
    }
    const erroFaixas = validarFaixasDescontoSindical(form);
    if (erroFaixas) return toast.error(erroFaixas);

    const rubricaSemana = rubricasHoraExtra.find((rubrica) => rubrica.id === form.rubrica_hora_extra_semana_id);
    const rubricaFimSemana = rubricasHoraExtra.find((rubrica) => rubrica.id === form.rubrica_hora_extra_fim_semana_id);
    const percentualSemana = percentualHoraExtra(rubricaSemana?.formula_tipo);
    const percentualFimSemana = percentualHoraExtra(rubricaFimSemana?.formula_tipo);
    if (
      percentualSemana !== null &&
      percentualFimSemana !== null &&
      percentualSemana > percentualFimSemana
    ) {
      return toast.error('A hora extra da semana não pode ter percentual maior que a de domingos/finais de semana');
    }

    try {
      const payload = {
        ...form,
        sindicato_patronal_id: form.sindicato_patronal_id || null,
        piso_salarial_geral: form.piso_salarial_geral,
        reajuste_anual_percentual: parseFloat(form.reajuste_anual_percentual) || null,
        ...buildDescontoSindicalPayload(form),
        rubrica_desconto_sindical_id: form.rubrica_desconto_sindical_id || null,
        rubrica_hora_extra_semana_id: form.rubrica_hora_extra_semana_id || null,
        rubrica_hora_extra_fim_semana_id: form.rubrica_hora_extra_fim_semana_id || null,
      };
      if (edit) {
        await axios.put(`${API}/api/calculos-dp/convencoes/${edit}`, payload, authHeader());
        toast.success('Convenção atualizada');
      } else {
        await axios.post(`${API}/api/calculos-dp/convencoes`, payload, authHeader());
        toast.success('Convenção criada');
      }
      setOpen(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao salvar convenção');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Excluir esta convenção?')) return;
    try {
      await axios.delete(`${API}/api/calculos-dp/convencoes/${id}`, authHeader());
      toast.success('Convenção excluída'); load();
    } catch (e) { toast.error('Erro ao excluir'); }
  };

  const aplicarDissidio = async () => {
    if (!dissidioForm.convencao_id) return toast.error('Convenção inválida para aplicar o dissídio');
    if (!dissidioForm.data_evento) return toast.error('Informe a data do dissídio');
    if (Number(dissidioForm.percentual || 0) <= 0) return toast.error('Informe um percentual maior que zero');
    if (dissidioForm.incluir_retroativo && !dissidioForm.competencia_pagamento_retroativo) {
      return toast.error('Informe a competência em que o retroativo será pago');
    }

    const [competenciaAno, competenciaMes] = (dissidioForm.competencia_pagamento_retroativo || '').split('-');
    const confirmou = await confirm({
      title: 'Aplicar dissídio?',
      description: dissidioForm.incluir_retroativo
        ? `Isso atualizará os salários vinculados à convenção e programará o retroativo para ${String(Number(competenciaMes || 0)).padStart(2, '0')}/${competenciaAno}.`
        : 'Isso atualizará os salários vinculados à convenção e registrará o histórico funcional.',
      confirmLabel: 'Aplicar dissídio',
    });
    if (!confirmou) return;

    setDissidioSaving(true);
    try {
      const response = await axios.post(
        `${API}/api/calculos-dp/convencoes/${dissidioForm.convencao_id}/aplicar-dissidio`,
        {
          data_evento: dissidioForm.data_evento,
          percentual: Number(dissidioForm.percentual || 0),
          aplicar_piso_salarial: !!dissidioForm.aplicar_piso_salarial,
          incluir_retroativo: !!dissidioForm.incluir_retroativo,
          competencia_pagamento_mes: dissidioForm.incluir_retroativo ? Number(competenciaMes || 0) : null,
          competencia_pagamento_ano: dissidioForm.incluir_retroativo ? Number(competenciaAno || 0) : null,
        },
        authHeader(),
      );
      setDissidioResult(response.data);
      toast.success(
        `Dissídio aplicado em ${response.data.total_atualizados} colaborador(es).`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao aplicar dissídio');
    } finally {
      setDissidioSaving(false);
    }
  };

  const addClausula = () => {
    setForm({
      ...form,
      clausulas: [...form.clausulas, { tipo: 'beneficio', titulo: '', descricao: '', valor: null, percentual: null, aplicavel_a: '' }]
    });
  };

  const updateClausula = (i, field, value) => {
    const arr = [...form.clausulas];
    arr[i] = { ...arr[i], [field]: value };
    setForm({ ...form, clausulas: arr });
  };

  const removeClausula = (i) => {
    setForm({ ...form, clausulas: form.clausulas.filter((_, idx) => idx !== i) });
  };

  // Input mask for DD/MM/AAAA format
  const formatInputValue = (value) => {
    if (!value) return '';

    // Extract only digits
    const digits = value.replace(/\D/g, '');

    if (digits.length <= 4) {
      // Year only (AAAA) - show as YYYY
      return digits.replace(/^(\d{4})$/, '$1');
    }
    else if (digits.length >= 8) {
      // DDMMYYYY format - auto format as DD/MM/YYYY
      const day = digits.substring(0, 2);
      const month = digits.substring(2, 4);
      const year = digits.substring(4, 8);

      // Validate day/month ranges
      const dayNum = parseInt(day, 10);
      const monthNum = parseInt(month, 10);

      // If day or month are invalid, return only digits for user to fix
      if (dayNum > 31 || monthNum > 12) {
        return digits;
      }

      return `${day}/${month}/${year}`;
    }
    else {
      // Partial input - show only digits
      return digits;
    }
  };

  // Parse input value to internal format
  const parseInputValue = (inputValue) => {
    if (!inputValue) return '';

    // Extract digits from formatted input
    const digits = inputValue.replace(/\D/g, '');

    if (digits.length === 4) {
      // Year only
      return digits;
    } else if (digits.length >= 8) {
      // Convert DDMMYYYY to internal format DD/MM/YYYY
      const day = digits.substring(0, 2);
      const month = digits.substring(2, 4);
      const year = digits.substring(4, 8);
      return `${day}/${month}/${year}`;
    }

    return inputValue;
  };

  // Validation helper for DD/MM/AAAA format
  const validateVigenciaInput = (vigencia) => {
    if (!vigencia) return null;
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(vigencia)) return 'Formato inválido (DD/MM/AAAA)';
    const [d, m, a] = vigencia.split('/').map(Number);
    if (m < 1 || m > 12) return 'Mês inválido';
    if (d < 1 || d > 31) return 'Dia inválido';
    const date = new Date(a, m - 1, d);
    if (date.getDate() !== d || date.getMonth() !== m - 1 || date.getFullYear() !== a) return 'Data inválida';
    return null;
  };

  // Validation error state
  const [validationError, setValidationError] = useState({
    vigencia_inicio: null,
    vigencia_fim: null,
  });

  // Update validation when form changes
  useEffect(() => {
    if (form.vigencia_inicio) {
      setValidationError(prev => ({
        ...prev,
        vigencia_inicio: validateVigenciaInput(form.vigencia_inicio),
      }));
    }
    if (form.vigencia_fim) {
      setValidationError(prev => ({
        ...prev,
        vigencia_fim: validateVigenciaInput(form.vigencia_fim),
      }));
    }
  }, [form.vigencia_inicio, form.vigencia_fim]);

  // Helper function for table display
  const displayVigenciaDate = (vigencia) => {
    if (!vigencia) return '—';
    if (vigencia.length === 10) {
      return vigencia; // DD/MM/YYYY
    } else if (vigencia.length === 4) {
      return vigencia; // YYYY
    }
    return vigencia;
  };

  const hoje = new Date().toISOString().slice(0, 10);

  // Helper function to parse year from DD/MM/YYYY format
  const parseYearFromVigencia = (vigencia) => {
    if (!vigencia) return null;
    if (vigencia.length === 10) {
      return parseInt(vigencia.substring(6, 10)) || null;
    }
    return null;
  };

  const statusBadge = (c) => {
    const vigenciaFimAno = parseYearFromVigencia(c.vigencia_fim);
    const vigenciaInicioAno = parseYearFromVigencia(c.vigencia_inicio);

    if (!vigenciaFimAno) return <Badge variant="outline">Sem data</Badge>;

    const hojeAno = hoje.substring(0, 4);
    const hojeAnoNum = parseInt(hojeAno, 10);

    if (vigenciaFimAno < hojeAnoNum) return <Badge variant="destructive">Vencida</Badge>;
    if (vigenciaFimAno === hojeAnoNum) return <Badge className="bg-amber-500">Vence este ano</Badge>;

    return <Badge className="bg-emerald-500">Vigente</Badge>;
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-600">{items.length} convenção(ões) cadastrada(s).</p>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/dp-folha/dissidios')}
          >
            <History className="h-4 w-4 mr-2" /> Histórico de Dissídios
          </Button>
          <Button
            onClick={openCreate}
            disabled={sindicatos.length === 0}
            data-testid="dp-cct-novo-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> Nova Convenção
          </Button>
        </div>
      </div>

      {sindicatos.length === 0 && (
        <Card className="border-amber-300">
          <CardContent className="p-3 text-sm text-amber-700 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Cadastre pelo menos um sindicato laboral antes de criar convenções.
          </CardContent>
        </Card>
      )}

      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600">
            <tr>
              <th className="text-left p-3">Nome</th>
              <th className="text-left p-3">Sind. Laboral</th>
              <th className="text-left p-3">Vigência</th>
              <th className="text-right p-3">Piso</th>
              <th className="text-right p-3">Reajuste</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="p-4 text-slate-500 text-center">Carregando...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="p-4 text-slate-500 text-center">Nenhuma convenção cadastrada.</td></tr>
            ) : items.map((c) => (
              <tr key={c.id} className="border-t hover:bg-slate-50">
                <td className="p-3 font-medium">{c.nome}</td>
                <td className="p-3 text-xs">{c.sindicato_laboral_nome || '—'}</td>
                <td className="p-3 text-xs">{fmtDate(c.vigencia_inicio)} → {fmtDate(c.vigencia_fim)}</td>
                <td className="p-3 text-right">{c.piso_salarial_geral ? fmtBRL(c.piso_salarial_geral) : '—'}</td>
                <td className="p-3 text-right">{c.reajuste_anual_percentual ? `${Number(c.reajuste_anual_percentual).toFixed(2).replace('.', ',')}%` : '—'}</td>
                <td className="p-3">{statusBadge(c)}</td>
                <td className="p-3 text-right">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => navigate(`/dp-folha/dissidios?convencao_id=${c.id}`)}
                    title="Ver histórico de dissídios"
                  >
                    <History className="h-4 w-4 text-slate-700" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => openDissidio(c)} title="Aplicar dissídio">
                    <Wallet className="h-4 w-4 text-emerald-700" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`dp-cct-edit-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(c.id)} data-testid={`dp-cct-delete-${c.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dp-cct-dialog">
          <DialogHeader>
            <DialogTitle>{edit ? 'Editar Convenção Coletiva' : 'Nova Convenção Coletiva'}</DialogTitle>
            <DialogDescription>Dados da CCT/ACT, vigência e cláusulas relevantes (piso salarial, reajuste, benefícios).</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Nome da Convenção *</Label>
              <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="dp-cct-nome" />
            </div>
            <div>
              <Label>Nº Registro MTE</Label>
              <Input value={form.numero_registro_mte} onChange={(e) => setForm({ ...form, numero_registro_mte: e.target.value })} />
            </div>
            <div>
              <Label>Data-base (MM-DD)</Label>
              <Input placeholder="01-01" value={form.data_base} onChange={(e) => setForm({ ...form, data_base: e.target.value })} />
            </div>
            <div>
              <Label>Sindicato Laboral *</Label>
              <Select value={form.sindicato_laboral_id} onValueChange={(v) => setForm({ ...form, sindicato_laboral_id: v })}>
                <SelectTrigger data-testid="dp-cct-sind-laboral"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>
                  {sindicatos.filter(s => s.tipo === 'laboral' || !s.tipo).map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.nome}{s.sigla ? ` (${s.sigla})` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Sindicato Patronal</Label>
              <Select value={form.sindicato_patronal_id || ''} onValueChange={(v) => setForm({ ...form, sindicato_patronal_id: v })}>
                <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>
                  {sindicatos.filter(s => s.tipo === 'patronal').map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.nome}{s.sigla ? ` (${s.sigla})` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Vigência Início *</Label>
              <DatePicker
                value={form.vigencia_inicio}
                onChange={(v) => setForm({ ...form, vigencia_inicio: v })}
                testId="dp-cct-vig-ini"
              />
            </div>
            <div>
              <Label>Vigência Fim *</Label>
              <DatePicker
                value={form.vigencia_fim}
                onChange={(v) => setForm({ ...form, vigencia_fim: v })}
                testId="dp-cct-vig-fim"
              />
            </div>
            <div className="col-span-2">
              <Label>Abrangência</Label>
              <Input placeholder="Estado, município, categoria..." value={form.abrangencia} onChange={(e) => setForm({ ...form, abrangencia: e.target.value })} />
            </div>
            <div>
              <Label>Piso Salarial (R$)</Label>
              <CurrencyInput value={form.piso_salarial_geral} onChange={v => setForm({ ...form, piso_salarial_geral: v })} />
            </div>
            <div>
              <Label>Reajuste Anual (%)</Label>
              <Input type="number" step="0.01" value={form.reajuste_anual_percentual} onChange={(e) => setForm({ ...form, reajuste_anual_percentual: e.target.value })} />
            </div>

            <div className="col-span-2 rounded-md border p-4 space-y-4 bg-slate-50">
              <div>
                <Label className="text-sm font-medium">Rubricas Padrão de Hora Extra</Label>
                <p className="text-xs text-slate-500">
                  Define quais rubricas de hora extra a folha deve usar para semana e para domingos/finais de semana.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Hora extra da semana</Label>
                  <Select
                    value={form.rubrica_hora_extra_semana_id || '__default_he_semana__'}
                    onValueChange={(v) => setForm({ ...form, rubrica_hora_extra_semana_id: v === '__default_he_semana__' ? '' : v })}
                  >
                    <SelectTrigger><SelectValue placeholder="Usar rubrica padrão" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__default_he_semana__">Usar rubrica padrão do sistema</SelectItem>
                      {rubricasHoraExtra.map((rubrica) => (
                        <SelectItem key={rubrica.id} value={rubrica.id}>
                          {rubrica.codigo} — {rubrica.descricao} ({Number(percentualHoraExtra(rubrica.formula_tipo) || 0).toFixed(2).replace('.', ',')}%)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Hora extra domingos/finais de semana</Label>
                  <Select
                    value={form.rubrica_hora_extra_fim_semana_id || '__default_he_fds__'}
                    onValueChange={(v) => setForm({ ...form, rubrica_hora_extra_fim_semana_id: v === '__default_he_fds__' ? '' : v })}
                  >
                    <SelectTrigger><SelectValue placeholder="Usar rubrica padrão" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__default_he_fds__">Usar rubrica padrão do sistema</SelectItem>
                      {rubricasHoraExtra.map((rubrica) => (
                        <SelectItem key={rubrica.id} value={rubrica.id}>
                          {rubrica.codigo} — {rubrica.descricao} ({Number(percentualHoraExtra(rubrica.formula_tipo) || 0).toFixed(2).replace('.', ',')}%)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-xs text-slate-500">
                Regra: o percentual da hora extra da semana nunca pode ser maior que o percentual configurado para domingos/finais de semana.
              </p>
            </div>

            <div className="col-span-2 rounded-md border p-4 space-y-4 bg-slate-50">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label className="text-sm font-medium">Desconto Sindical Automático</Label>
                  <p className="text-xs text-slate-500">
                    Lança automaticamente o desconto na folha mensal de todos os colaboradores vinculados a esta convenção.
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={!!form.desconto_sindical_ativo}
                  onChange={(e) => setForm({ ...form, desconto_sindical_ativo: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300"
                />
              </div>

              {form.desconto_sindical_ativo && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Tipo</Label>
                    <Select value={form.desconto_sindical_tipo || 'fixo'} onValueChange={(v) => setForm({ ...form, desconto_sindical_tipo: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fixo">Valor fixo</SelectItem>
                        <SelectItem value="percentual">Percentual sobre salário base</SelectItem>
                        <SelectItem value="faixa">Por faixa salarial</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {form.desconto_sindical_tipo !== 'faixa' && (
                    <div>
                      <Label>{form.desconto_sindical_tipo === 'percentual' ? 'Percentual (%)' : 'Valor mensal (R$)'}</Label>
                      {form.desconto_sindical_tipo === 'percentual' ? (
                        <PercentInput value={form.desconto_sindical_valor} onChange={(v) => setForm({ ...form, desconto_sindical_valor: v })} />
                      ) : (
                        <CurrencyInput value={form.desconto_sindical_valor} onChange={(v) => setForm({ ...form, desconto_sindical_valor: v })} />
                      )}
                    </div>
                  )}
                  {form.desconto_sindical_tipo === 'percentual' && (
                    <div>
                      <Label>Teto máximo do desconto (R$)</Label>
                      <CurrencyInput value={form.desconto_sindical_teto_valor} onChange={(v) => setForm({ ...form, desconto_sindical_teto_valor: v })} />
                    </div>
                  )}
                  {form.desconto_sindical_tipo === 'faixa' && (
                    <DescontoSindicalFaixasEditor form={form} setForm={setForm} />
                  )}
                  <div>
                    <Label>Rubrica de desconto</Label>
                    <Select
                      value={form.rubrica_desconto_sindical_id || '__default_rubrica__'}
                      onValueChange={(v) => setForm({ ...form, rubrica_desconto_sindical_id: v === '__default_rubrica__' ? '' : v })}
                    >
                      <SelectTrigger><SelectValue placeholder="Usar rubrica padrão" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__default_rubrica__">Usar rubrica padrão</SelectItem>
                        {rubricasDesconto.map((rubrica) => (
                          <SelectItem key={rubrica.id} value={rubrica.id}>{rubrica.codigo} — {rubrica.descricao}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className={form.desconto_sindical_tipo === 'percentual' ? '' : 'col-span-2'}>
                    <Label>Descrição complementar</Label>
                    <Input value={form.desconto_sindical_descricao || ''} onChange={(e) => setForm({ ...form, desconto_sindical_descricao: e.target.value })} placeholder="Ex.: Mensalidade sindical Neuro" />
                  </div>
                </div>
              )}
            </div>

            {/* Cláusulas estruturadas */}
            <div className="col-span-2 mt-2">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-base">Cláusulas Estruturadas</Label>
                <Button type="button" size="sm" variant="outline" onClick={addClausula} data-testid="dp-cct-add-clausula">
                  <Plus className="h-3 w-3 mr-1" /> Adicionar Cláusula
                </Button>
              </div>
              {form.clausulas.length === 0 ? (
                <p className="text-xs text-slate-500 italic">Nenhuma cláusula adicionada.</p>
              ) : (
                <div className="space-y-2">
                  {form.clausulas.map((cl, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-end p-2 border rounded bg-slate-50">
                      <div className="col-span-3">
                        <Label className="text-xs">Tipo</Label>
                        <Select value={cl.tipo} onValueChange={(v) => updateClausula(i, 'tipo', v)}>
                          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="piso_salarial">Piso Salarial</SelectItem>
                            <SelectItem value="reajuste">Reajuste</SelectItem>
                            <SelectItem value="hora_extra">Hora Extra</SelectItem>
                            <SelectItem value="beneficio">Benefício</SelectItem>
                            <SelectItem value="outros">Outros</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-4">
                        <Label className="text-xs">Título</Label>
                        <Input className="h-9" value={cl.titulo} onChange={(e) => updateClausula(i, 'titulo', e.target.value)} />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Valor (R$)</Label>
                        <CurrencyInput className="h-9" value={cl.valor} onChange={v => updateClausula(i, 'valor', v)} />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">%</Label>
                        <Input className="h-9" type="number" step="0.01" value={cl.percentual ?? ''} onChange={(e) => updateClausula(i, 'percentual', e.target.value === '' ? null : parseFloat(e.target.value))} />
                      </div>
                      <div className="col-span-1">
                        <Button type="button" size="sm" variant="ghost" onClick={() => removeClausula(i)}>
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                      <div className="col-span-12">
                        <Label className="text-xs">Descrição</Label>
                        <Input className="h-9" value={cl.descricao || ''} onChange={(e) => updateClausula(i, 'descricao', e.target.value)} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="col-span-2">
              <Label>Observações</Label>
              <Textarea value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            {edit && (
              <Button
                type="button"
                variant="outline"
                onClick={() => openDissidio({ id: edit, ...form })}
                disabled={Number(form.reajuste_anual_percentual || 0) <= 0}
              >
                <Wallet className="h-4 w-4 mr-2" /> Aplicar Dissídio
              </Button>
            )}
            <Button onClick={save} data-testid="dp-cct-salvar-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dissidioOpen} onOpenChange={setDissidioOpen}>
        <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Aplicar Dissídio</DialogTitle>
            <DialogDescription>
              Atualiza o salário dos colaboradores vinculados a esta convenção, registra histórico funcional e, se necessário, programa o retroativo na folha.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-md border bg-slate-50 p-4 text-sm space-y-1">
              <div><span className="font-medium">Convenção:</span> {dissidioForm.convencao_nome || '—'}</div>
              <div><span className="font-medium">Percentual base:</span> {Number(dissidioForm.percentual || 0).toFixed(2).replace('.', ',')}%</div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Data de vigência do dissídio</Label>
                <Input
                  type="date"
                  value={dissidioForm.data_evento}
                  onChange={(e) => setDissidioForm({ ...dissidioForm, data_evento: e.target.value })}
                />
              </div>
              <div>
                <Label>Percentual aplicado (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={dissidioForm.percentual}
                  onChange={(e) => setDissidioForm({ ...dissidioForm, percentual: e.target.value })}
                />
              </div>
            </div>

            <label className="flex items-center gap-3 rounded-md border p-3 text-sm">
              <input
                type="checkbox"
                checked={!!dissidioForm.aplicar_piso_salarial}
                onChange={(e) => setDissidioForm({ ...dissidioForm, aplicar_piso_salarial: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300"
              />
              <span>Enquadrar automaticamente no piso salarial da convenção, se aplicável.</span>
            </label>

            <label className="flex items-center gap-3 rounded-md border p-3 text-sm">
              <input
                type="checkbox"
                checked={!!dissidioForm.incluir_retroativo}
                onChange={(e) => setDissidioForm({
                  ...dissidioForm,
                  incluir_retroativo: e.target.checked,
                  competencia_pagamento_retroativo: e.target.checked
                    ? dissidioForm.competencia_pagamento_retroativo
                    : '',
                })}
                className="h-4 w-4 rounded border-gray-300"
              />
              <span>Gerar retroativo para pagar em uma competência futura da folha.</span>
            </label>

            {dissidioForm.incluir_retroativo && (
              <div>
                <Label>Competência de pagamento do retroativo</Label>
                <Input
                  type="month"
                  value={dissidioForm.competencia_pagamento_retroativo}
                  onChange={(e) => setDissidioForm({ ...dissidioForm, competencia_pagamento_retroativo: e.target.value })}
                />
                <p className="mt-1 text-xs text-slate-500">
                  O sistema calcula as diferenças entre a data do dissídio e a competência anterior ao pagamento, e lança o total automaticamente no holerite desse mês.
                </p>
              </div>
            )}

            {dissidioResult && (
              <div className="space-y-3 rounded-md border p-4 bg-emerald-50">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <div><span className="font-medium">Atualizados:</span> {dissidioResult.total_atualizados}</div>
                  <div><span className="font-medium">Ignorados:</span> {dissidioResult.total_ignorados}</div>
                  <div><span className="font-medium">Data:</span> {fmtDate(dissidioResult.data_evento)}</div>
                </div>
                {dissidioResult.incluir_retroativo && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                    <div><span className="font-medium">Retroativo total:</span> {fmtBRL(dissidioResult.total_retroativo)}</div>
                    <div><span className="font-medium">Colabs. c/ retroativo:</span> {dissidioResult.total_colaboradores_com_retroativo}</div>
                    <div>
                      <span className="font-medium">Pagamento:</span>{' '}
                      {dissidioResult.competencia_pagamento_mes && dissidioResult.competencia_pagamento_ano
                        ? `${String(dissidioResult.competencia_pagamento_mes).padStart(2, '0')}/${dissidioResult.competencia_pagamento_ano}`
                        : '—'}
                    </div>
                  </div>
                )}
                <div className="max-h-64 overflow-y-auto rounded border bg-white">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                      <tr>
                        <th className="text-left p-2">Colaborador</th>
                        <th className="text-right p-2">Anterior</th>
                        <th className="text-right p-2">Novo</th>
                        <th className="text-right p-2">%</th>
                        <th className="text-right p-2">Retroativo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(dissidioResult.atualizados || []).map((item) => (
                        <tr key={item.employee_id} className="border-t">
                          <td className="p-2">{item.employee_nome || item.employee_id}</td>
                          <td className="p-2 text-right">{fmtBRL(item.salario_anterior)}</td>
                          <td className="p-2 text-right">{fmtBRL(item.salario_novo)}</td>
                          <td className="p-2 text-right">{Number(item.percentual_aplicado || 0).toFixed(2).replace('.', ',')}%</td>
                          <td className="p-2 text-right">{fmtBRL(item.retroativo_total || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDissidioOpen(false)}>Fechar</Button>
            <Button onClick={aplicarDissidio} disabled={dissidioSaving}>
              {dissidioSaving ? 'Aplicando...' : 'Aplicar Dissídio'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog />
    </div>
  );
};

// ============================================================================
// TAB: HISTÓRICO DE DISSÍDIOS
// ============================================================================
export const DissidiosTab = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialConvencaoId = searchParams.get('convencao_id') || '__all__';

  const [items, setItems] = useState([]);
  const [convencoes, setConvencoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [filters, setFilters] = useState({
    convencao_id: initialConvencaoId,
    com_retroativo: '__all__',
  });

  const loadBase = useCallback(async () => {
    const r = await axios.get(`${API}/api/calculos-dp/convencoes`, authHeader());
    setConvencoes(r.data || []);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.convencao_id && filters.convencao_id !== '__all__') {
        params.convencao_id = filters.convencao_id;
      }
      if (filters.com_retroativo === 'sim') params.com_retroativo = true;
      if (filters.com_retroativo === 'nao') params.com_retroativo = false;
      const r = await axios.get(`${API}/api/calculos-dp/dissidios/aplicados`, {
        ...authHeader(),
        params,
      });
      setItems(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao carregar histórico de dissídios');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadBase(); }, [loadBase]);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (loteId) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const r = await axios.get(`${API}/api/calculos-dp/dissidios/aplicados/${loteId}`, authHeader());
      setDetail(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao carregar detalhes do dissídio');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const loteRetroativoStatus = (lote) => {
    const totalRetroativo = Number(lote?.total_retroativo || 0);
    if (totalRetroativo <= 0) return <Badge variant="outline">Sem retroativo</Badge>;
    const total = Number(lote?.retroativos_total || 0);
    const lancados = Number(lote?.retroativos_lancados || 0);
    if (total > 0 && lancados >= total) return <Badge className="bg-emerald-600">Lançado em folha</Badge>;
    if (lancados > 0) return <Badge className="bg-blue-600">Lançamento parcial</Badge>;
    return <Badge className="bg-amber-500">Retroativo programado</Badge>;
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>Convenção</Label>
              <Select value={filters.convencao_id} onValueChange={(v) => setFilters({ ...filters, convencao_id: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todas as convenções</SelectItem>
                  {convencoes.map((conv) => (
                    <SelectItem key={conv.id} value={conv.id}>{conv.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Retroativo</Label>
              <Select value={filters.com_retroativo} onValueChange={(v) => setFilters({ ...filters, com_retroativo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos</SelectItem>
                  <SelectItem value="sim">Com retroativo</SelectItem>
                  <SelectItem value="nao">Sem retroativo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end justify-end gap-2">
              <Button variant="outline" onClick={load}>
                <RefreshCw className="h-4 w-4 mr-2" /> Atualizar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600">
            <tr>
              <th className="text-left p-3">Data vigência</th>
              <th className="text-left p-3">Convenção</th>
              <th className="text-right p-3">%</th>
              <th className="text-right p-3">Atualizados</th>
              <th className="text-right p-3">Retroativo total</th>
              <th className="text-left p-3">Pagamento</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="p-4 text-center text-slate-500">Carregando...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={8} className="p-4 text-center text-slate-500">Nenhum dissídio aplicado encontrado.</td></tr>
            ) : items.map((lote) => (
              <tr key={lote.id} className="border-t hover:bg-slate-50">
                <td className="p-3">{fmtDate(lote.data_evento)}</td>
                <td className="p-3 font-medium">{lote.convencao_nome || '—'}</td>
                <td className="p-3 text-right">{Number(lote.percentual_aplicado || 0).toFixed(2).replace('.', ',')}%</td>
                <td className="p-3 text-right">{lote.total_atualizados || 0}</td>
                <td className="p-3 text-right">{fmtBRL(lote.total_retroativo || 0)}</td>
                <td className="p-3">{fmtCompetencia(lote.competencia_pagamento_mes, lote.competencia_pagamento_ano)}</td>
                <td className="p-3">{loteRetroativoStatus(lote)}</td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => openDetail(lote.id)}>
                    <Eye className="h-4 w-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="w-[96vw] max-w-6xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalhe do Dissídio</DialogTitle>
            <DialogDescription>
              Histórico salarial gerado pelo lote, com retroativos programados ou lançados em folha.
            </DialogDescription>
          </DialogHeader>

          {detailLoading ? (
            <div className="py-10 text-center text-slate-500">Carregando detalhes...</div>
          ) : !detail ? (
            <div className="py-10 text-center text-slate-500">Nenhum detalhe disponível.</div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Card><CardContent className="p-4"><div className="text-xs text-slate-500">Convenção</div><div className="font-medium">{detail.lote?.convencao_nome || '—'}</div></CardContent></Card>
                <Card><CardContent className="p-4"><div className="text-xs text-slate-500">Vigência</div><div className="font-medium">{fmtDate(detail.lote?.data_evento)}</div></CardContent></Card>
                <Card><CardContent className="p-4"><div className="text-xs text-slate-500">Reajuste</div><div className="font-medium">{Number(detail.lote?.percentual_aplicado || 0).toFixed(2).replace('.', ',')}%</div></CardContent></Card>
                <Card><CardContent className="p-4"><div className="text-xs text-slate-500">Retroativo</div><div className="font-medium">{fmtBRL(detail.lote?.total_retroativo || 0)}</div></CardContent></Card>
              </div>

              <div className="rounded-md border">
                <div className="border-b bg-slate-50 p-3 text-sm font-medium">Alterações salariais</div>
                <div className="max-h-72 overflow-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-white text-xs uppercase text-slate-500">
                      <tr>
                        <th className="text-left p-3">Colaborador</th>
                        <th className="text-right p-3">Salário anterior</th>
                        <th className="text-right p-3">Novo salário</th>
                        <th className="text-right p-3">% aplicado</th>
                        <th className="text-right p-3">Retroativo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detail.historicos || []).length === 0 ? (
                        <tr><td colSpan={5} className="p-4 text-center text-slate-500">Nenhum histórico encontrado.</td></tr>
                      ) : (detail.historicos || []).map((hist) => (
                        <tr key={hist.id} className="border-t">
                          <td className="p-3">{hist.employee?.nome || hist.employee_id}</td>
                          <td className="p-3 text-right">{fmtBRL(hist.valores_anteriores?.salario)}</td>
                          <td className="p-3 text-right">{fmtBRL(hist.valores_novos?.salario)}</td>
                          <td className="p-3 text-right">{Number(hist.valores_novos?.percentual_aplicado || 0).toFixed(2).replace('.', ',')}%</td>
                          <td className="p-3 text-right">{fmtBRL(hist.valores_novos?.retroativo_total || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="rounded-md border">
                <div className="border-b bg-slate-50 p-3 text-sm font-medium">Retroativos</div>
                <div className="max-h-80 overflow-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-white text-xs uppercase text-slate-500">
                      <tr>
                        <th className="text-left p-3">Colaborador</th>
                        <th className="text-left p-3">Competência de pagamento</th>
                        <th className="text-left p-3">Referências</th>
                        <th className="text-right p-3">Dif. mensal</th>
                        <th className="text-right p-3">Total</th>
                        <th className="text-left p-3">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detail.retroativos || []).length === 0 ? (
                        <tr><td colSpan={6} className="p-4 text-center text-slate-500">Nenhum retroativo neste lote.</td></tr>
                      ) : (detail.retroativos || []).map((retro) => (
                        <tr key={retro.id} className="border-t align-top">
                          <td className="p-3">{retro.employee?.nome || retro.employee_id}</td>
                          <td className="p-3">{fmtCompetencia(retro.competencia_pagamento_mes, retro.competencia_pagamento_ano)}</td>
                          <td className="p-3 text-xs text-slate-600">
                            {(retro.referencias || []).map((ref) => `${ref.referencia || fmtCompetencia(ref.mes, ref.ano)}: ${fmtBRL(ref.valor)}`).join(' • ')}
                          </td>
                          <td className="p-3 text-right">{fmtBRL(retro.diferenca_mensal)}</td>
                          <td className="p-3 text-right">{fmtBRL(retro.total_retroativo)}</td>
                          <td className="p-3">
                            {retro.status === 'lancado'
                              ? <Badge className="bg-emerald-600">Lançado em folha</Badge>
                              : <Badge className="bg-amber-500">Pendente</Badge>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailOpen(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================================================
// TAB: CENTROS DE CUSTO (CRUD)
// ============================================================================
export const CentrosCustoTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const empty = {
    codigo: '', nome: '', tipo: 'operacional',
    descricao: '', rateio_padrao_percentual: 100,
  };
  const [form, setForm] = useState(empty);
  const { confirm, ConfirmDialog } = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/calculos-dp/centros-custo`, authHeader());
      setItems(r.data || []);
    } catch (e) { toast.error('Erro ao listar centros de custo'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEdit(null); setForm(empty); setOpen(true); };
  const openEdit = (s) => { setEdit(s.id); setForm({ ...empty, ...s }); setOpen(true); };

  const save = async () => {
    if (!form.codigo || !form.nome) return toast.error('Código e nome são obrigatórios');
    try {
      if (edit) {
        await axios.put(`${API}/api/calculos-dp/centros-custo/${edit}`, form, authHeader());
        toast.success('Centro de custo atualizado');
      } else {
        await axios.post(`${API}/api/calculos-dp/centros-custo`, form, authHeader());
        toast.success('Centro de custo criado');
      }
      setOpen(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao salvar centro de custo');
    }
  };

  const remove = async (id) => {
    if (!await confirm({ title: 'Excluir Centro de Custo?', description: 'Esta ação não pode ser desfeita.', destructive: true, confirmLabel: 'Excluir' })) return;
    try {
      await axios.delete(`${API}/api/calculos-dp/centros-custo/${id}`, authHeader());
      toast.success('Centro de custo excluído'); load();
    } catch (e) { toast.error('Erro ao excluir'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-600">{items.length} centro(s) cadastrado(s).</p>
        <Button onClick={openCreate} data-testid="dp-cc-novo-btn">
          <Plus className="h-4 w-4 mr-2" /> Novo Centro de Custo
        </Button>
      </div>
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600">
            <tr>
              <th className="text-left p-3">Código</th>
              <th className="text-left p-3">Nome</th>
              <th className="text-left p-3">Tipo</th>
              <th className="text-right p-3">Rateio</th>
              <th className="text-right p-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="p-4 text-slate-500 text-center">Carregando...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={5} className="p-4 text-slate-500 text-center">Nenhum centro de custo cadastrado.</td></tr>
            ) : items.map((c) => (
              <tr key={c.id} className="border-t hover:bg-slate-50">
                <td className="p-3 font-mono">{c.codigo}</td>
                <td className="p-3 font-medium">{c.nome}</td>
                <td className="p-3"><Badge variant="outline">{c.tipo}</Badge></td>
                <td className="p-3 text-right">{Number(c.rateio_padrao_percentual).toFixed(2)}%</td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`dp-cc-edit-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(c.id)} data-testid={`dp-cc-delete-${c.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="dp-cc-dialog">
          <DialogHeader>
            <DialogTitle>{edit ? 'Editar Centro de Custo' : 'Novo Centro de Custo'}</DialogTitle>
            <DialogDescription>Estrutura usada para rateio de folha e relatórios gerenciais.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Código *</Label>
              <Input value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })} data-testid="dp-cc-codigo" />
            </div>
            <div>
              <Label>Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="operacional">Operacional</SelectItem>
                  <SelectItem value="administrativo">Administrativo</SelectItem>
                  <SelectItem value="comercial">Comercial</SelectItem>
                  <SelectItem value="projeto">Projeto</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Nome *</Label>
              <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="dp-cc-nome" />
            </div>
            <div className="col-span-2">
              <Label>Descrição</Label>
              <Textarea value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
            </div>
            <div>
              <Label>Rateio (%)</Label>
              <div className="relative">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={form.rateio_padrao_percentual}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value) || 0;
                    setForm({ ...form, rateio_padrao_percentual: Math.min(Math.max(val, 0), 100) });
                  }}
                  onKeyDown={(e) => { if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault(); }}
                  className="pr-8"
                  data-testid="dp-cc-rateio"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">%</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} data-testid="dp-cc-salvar-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog />
    </div>
  );
};

// ============================================================================
// TAB: CERTIFICADOS DIGITAIS (Upload + Listagem)
// ============================================================================
export const CertificadosTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [empresas, setEmpresas] = useState([]);
  const [form, setForm] = useState({
    empresa_id: '', tipo: 'A1', senha: '', observacoes: '', arquivo: null,
  });
  const [uploading, setUploading] = useState(false);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/calculos-dp/certificados`, authHeader());
      setItems(r.data || []);
    } catch (e) { toast.error('Erro ao listar certificados'); }
    finally { setLoading(false); }
  }, []);

  const loadEmpresas = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/companies`, authHeader());
      setEmpresas(r.data || []);
    } catch (e) { /* silencioso */ }
  }, []);

  useEffect(() => { loadItems(); loadEmpresas(); }, [loadItems, loadEmpresas]);

  const upload = async () => {
    if (!form.empresa_id || !form.senha || !form.arquivo) {
      return toast.error('Empresa, arquivo .pfx e senha são obrigatórios');
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('empresa_id', form.empresa_id);
      fd.append('tipo', form.tipo);
      fd.append('senha', form.senha);
      if (form.observacoes) fd.append('observacoes', form.observacoes);
      fd.append('arquivo', form.arquivo);

      await axios.post(`${API}/api/calculos-dp/certificados/upload`, fd, {
        ...authHeader(),
        headers: { ...authHeader().headers, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Certificado enviado com sucesso');
      setOpen(false);
      setForm({ empresa_id: '', tipo: 'A1', senha: '', observacoes: '', arquivo: null });
      loadItems();
    } catch (e) {
      let msg = 'Erro ao enviar certificado';
      if (e?.response?.data?.detail) {
        if (Array.isArray(e.response.data.detail)) {
          msg = e.response.data.detail.map(err => err.msg).join(', ');
        } else if (typeof e.response.data.detail === 'string') {
          msg = e.response.data.detail;
        }
      }
      toast.error(msg);
    } finally { setUploading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Excluir este certificado?')) return;
    try {
      await axios.delete(`${API}/api/calculos-dp/certificados/${id}`, authHeader());
      toast.success('Certificado excluído'); loadItems();
    } catch (e) { toast.error('Erro ao excluir'); }
  };

  const statusBadge = (s, dias) => {
    if (s === 'vencido' || (dias !== null && dias < 0)) return <Badge variant="destructive">Vencido</Badge>;
    if (dias !== null && dias <= 30) return <Badge className="bg-amber-500">Vence em {dias}d</Badge>;
    if (s === 'ativo') return <Badge className="bg-emerald-500">Ativo</Badge>;
    return <Badge variant="outline">{s}</Badge>;
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-600">{items.length} certificado(s) cadastrado(s).</p>
        <Button onClick={() => setOpen(true)} data-testid="dp-cert-novo-btn">
          <Upload className="h-4 w-4 mr-2" /> Enviar Certificado A1
        </Button>
      </div>

      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600">
            <tr>
              <th className="text-left p-3">Titular</th>
              <th className="text-left p-3">CNPJ/CPF</th>
              <th className="text-left p-3">Tipo</th>
              <th className="text-left p-3">Validade</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="p-4 text-slate-500 text-center">Carregando...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="p-4 text-slate-500 text-center">Nenhum certificado cadastrado.</td></tr>
            ) : items.map((c) => (
              <tr key={c.id} className="border-t hover:bg-slate-50">
                <td className="p-3 font-medium">{c.nome_titular}</td>
                <td className="p-3 font-mono text-xs">{c.cpf_cnpj_titular}</td>
                <td className="p-3"><Badge variant="outline">{c.tipo}</Badge></td>
                <td className="p-3">{fmtDate(c.validade_inicio)} → {fmtDate(c.validade_fim)}</td>
                <td className="p-3">{statusBadge(c.status, c.dias_para_vencer)}</td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => remove(c.id)} data-testid={`dp-cert-delete-${c.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="dp-cert-dialog" className="max-w-[70vw]">
          <DialogHeader>
            <DialogTitle>Enviar Certificado Digital A1</DialogTitle>
            <DialogDescription>Arquivo .pfx e senha são criptografados em repouso. O sistema extrai automaticamente titular, validade, emissor e serial.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Empresa *</Label>
              <Select value={form.empresa_id} onValueChange={(v) => setForm({ ...form, empresa_id: v })}>
                <SelectTrigger data-testid="dp-cert-empresa" className="truncate"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>
                  {empresas.map((e) => (
                    <SelectItem key={e.id} value={e.id} className="truncate">{e.nome_fantasia || e.razao_social || e.nome || e.id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Arquivo (.pfx) *</Label>
              <Input
                type="file"
                accept=".pfx,.p12"
                onChange={(e) => setForm({ ...form, arquivo: e.target.files?.[0] || null })}
                data-testid="dp-cert-file"
                className="overflow-hidden truncate"
              />
            </div>
            <div>
              <Label>Senha do certificado *</Label>
              <Input
                type="password"
                value={form.senha}
                onChange={(e) => setForm({ ...form, senha: e.target.value })}
                data-testid="dp-cert-senha"
                className="overflow-hidden truncate"
              />
            </div>
            <div>
              <Label>Observações</Label>
              <Textarea value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} className="overflow-hidden" />
            </div>
            <div className="bg-slate-50 border rounded p-3 text-xs text-slate-600">
              <ShieldCheck className="h-4 w-4 inline-block mr-1 text-emerald-600" />
              O arquivo e a senha são criptografados (Fernet, AES-128) antes de salvar no banco.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={uploading}>Cancelar</Button>
            <Button onClick={upload} disabled={uploading} data-testid="dp-cert-upload-btn">
              {uploading ? 'Enviando...' : 'Enviar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================================================
// PAGE — Roteamento por seção (uma seção por rota, via prop `section`)
// ============================================================================
const SECTIONS = {
  dashboard: { title: 'Visão Geral', subtitle: 'Status legislativo, contadores e alertas do Departamento Pessoal.', Component: DashboardTab },
  legislacao: { title: 'Legislação Vigente', subtitle: 'Tabelas INSS, IRRF, FGTS, Salário Mínimo, Salário-Família e VT atualmente em vigor.', Component: LegislacaoTab },
  sindicatos: { title: 'Sindicatos', subtitle: 'Sindicatos laborais e patronais cadastrados.', Component: SindicatosTab },
  convencoes: { title: 'Convenções Coletivas (CCT)', subtitle: 'CCTs/ACTs com vigência, pisos salariais e cláusulas estruturadas.', Component: ConvencoesTab },
  dissidios: { title: 'Histórico de Dissídios', subtitle: 'Lotes aplicados, alterações salariais e retroativos programados ou lançados em folha.', Component: DissidiosTab },
  'centros-custo': { title: 'Centros de Custo', subtitle: 'Estrutura para rateio de folha e relatórios gerenciais.', Component: CentrosCustoTab },
  'cadastro-trab': { title: 'Cadastro Trabalhista', subtitle: 'Vínculo eSocial, CBO, vínculos DP, estágio/aprendiz e dependentes.', Component: CadastroTrabalhistaTab },
  saude: { title: 'Saúde Ocupacional', subtitle: 'ASO, EPI, alertas de vencimento.', Component: SaudeOcupacionalTab },
  disciplinar: { title: 'Disciplinar & Histórico', subtitle: 'Medidas disciplinares + histórico funcional.', Component: DisciplinarHistoricoTab },
  folha: { title: 'Folha de Pagamento', subtitle: 'Rubricas, competências mensais e holerites com memória de cálculo.', Component: FolhaPagamentoTab },
  rescisoes: { title: '13º · Férias · Rescisão', subtitle: 'Cálculos de 13º (1ª/2ª parcela), férias com abono e TRCT (rescisão).', Component: Decimo13FeriasRescisaoTab },
  cnab240: { title: 'Remessa Bancária CNAB 240', subtitle: 'Geração de arquivos FEBRABAN v10.7 para envio ao banco.', Component: Cnab240Tab },
  'pj-pagamentos': { title: 'Pagamentos PJ', subtitle: 'Pagamentos a colaboradores Pessoa Jurídica — avulsos ou fixos, com recibo PDF.', Component: PjPagamentosTab },
  certificados: { title: 'Certificados Digitais', subtitle: 'Upload e gestão de certificados A1/A3 para integração eSocial.', Component: CertificadosTab },
  consignado: { title: 'Consignado', subtitle: 'Empréstimos consignados por colaborador — desconto automático na folha, férias ou rescisão.', Component: ConsignadoTab },
  afastamentos: { title: 'Afastamentos', subtitle: 'Relatório de afastamentos ativos e retornados registrados no histórico funcional.', Component: AfastamentoTab }
};

const DPFolhaConfigPage = ({ section: sectionProp }) => {
  const location = useLocation();
  const pathPart = location.pathname.replace(/\/$/, '').split('/').pop();
  const section = pathPart && pathPart !== 'dp-folha' && SECTIONS[pathPart] ? pathPart : (sectionProp || 'dashboard');
  const cfg = SECTIONS[section] || SECTIONS.dashboard;
  const Comp = cfg.Component;
  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid={`dp-folha-page-${section}`}>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <Building2 className="h-6 w-6 text-indigo-600" />
          DP / Folha — {cfg.title}
        </h1>
        <p className="text-sm text-slate-500 mt-1">{cfg.subtitle}</p>
      </div>
      <Comp />
    </div>
  );
};

export default DPFolhaConfigPage;
